/* ═══════════════════════════════════════════════════════════════
   TAO Clean PDP — interactions for everything below the hero.

   Owns: scroll reveals, the vibration counter, concern tabs, the
   gentle-mode toggle, the whitening wipe, review filters, the color
   gallery (built from THEMES; clicking a card re-themes the page
   through main.js via the 'tao:set-theme' event), theme-following
   imagery (detail crops / box / finale), tier pricing, the sticky
   add-to-cart bar, and the mock email capture.
   ═══════════════════════════════════════════════════════════════ */
import { THEMES as THEME_DEFINITIONS, DEFAULT_THEME } from './tao-mockup-themes.js?v=19colors2';
import { PACKAGING_VIDEO, PROBLEM_SCENE_VIDEO } from './tao-mockup-video-urls.js?v=3';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { createAmbientVideo } from './tao-video-playback.js?v=1';
import { createDockingPlayer } from './tao-mockup-docking.js?v=6';

const $  = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
/* Read live: the OS toggle can flip mid-visit, and every branch that checks RM at call
   time (image fades, the scene clip, the rails, the modal) follows it. Branches that
   decide once at boot (the counters, the wipe, the ambient loops) keep their boot answer. */
const RM_QUERY = matchMedia('(prefers-reduced-motion: reduce)');
let RM = RM_QUERY.matches;
RM_QUERY.addEventListener('change', (e) => { RM = e.matches; });
/* Save-Data is the other budget switch: colourway warming, the scene clip, the feature
   badges and the rails all defer to it the way the ambient loops already do. */
const checkSaveData = () => Boolean(navigator.connection?.saveData);
/* work that must still happen, just not on this task: a capped idle callback, else a
   short timer where the API is missing (Safari) */
const idle = (fn, timeout = 2000) => (window.requestIdleCallback
  ? requestIdleCallback(fn, { timeout })
  : setTimeout(fn, Math.min(timeout, 250)));
const cancelIdle = (id) => (window.cancelIdleCallback ? cancelIdleCallback(id) : clearTimeout(id));

/* assetPlates colorways serve their imagery from theme assets — the assets
   directory is wherever this module was served from (mirrors main.js) */
const ASSET_BASE = new URL('.', import.meta.url).href;

/* ── the Paige photo gallery ─────────────────────────────────────────────────
   The Find-your-color slots show real photographs from the studio shoot, five
   per colorway, shipped as theme assets (tao-mockup-paige-<id>-<slot>.jpg).
   Every colorway carries all five slots; the three poses the main shoot
   skipped are filled from its alternate takes (Pure White's own dock from the
   pack shot, a pale-blue in-hand for Glacier Blue, a neutral closeup for
   London Fog). The 'lean' slot name is historical: it now holds the
   docked-in-base pose, because the leaning shot read as a second pack shot. */
const PAIGE = {
  'cappuccino-taupe': ['base', 'big', 'handle', 'head', 'lean'],
  'champagne': ['base', 'big', 'handle', 'head', 'lean'],
  'champagne-pearl': ['base', 'big', 'handle', 'head', 'lean'],
  'desert-sage': ['base', 'big', 'handle', 'head', 'lean'],
  'eclipse': ['base', 'big', 'handle', 'head', 'lean'],
  'evergreen': ['base', 'big', 'handle', 'head', 'lean'],
  'glacier-blue': ['base', 'big', 'handle', 'head', 'lean'],
  'lilac': ['base', 'big', 'handle', 'head', 'lean'],
  'london-fog': ['base', 'big', 'handle', 'head', 'lean'],
  'marbel': ['base', 'big', 'handle', 'head', 'lean'],
  'marine-layer': ['base', 'big', 'handle', 'head', 'lean'],
  'matte-black': ['base', 'big', 'handle', 'head', 'lean'],
  'metallic-teal': ['base', 'big', 'handle', 'head', 'lean'],
  'midnight-blue': ['base', 'big', 'handle', 'head', 'lean'],
  'pink': ['base', 'big', 'handle', 'head', 'lean'],
  'rose-gold': ['base', 'big', 'handle', 'head', 'lean'],
  'sea-glass': ['base', 'big', 'handle', 'head', 'lean'],
  'smoky-quartz': ['base', 'big', 'handle', 'head', 'lean'],
  'watercolor-sky': ['base', 'big', 'handle', 'head', 'lean'],
};
/* Hand-built URLs get no asset digest, so replacing a photo's bytes leaves
   every cache holding the old picture — bump PAIGE_VER with any reshoot. */
const PAIGE_VER = 5;
const paigeURL = (id, slot) => (PAIGE[id]?.includes(slot)
  ? `${ASSET_BASE}tao-mockup-paige-${id}-${slot}.jpg?v=${PAIGE_VER}`
  : null);

const variantDataNode = document.querySelector('[data-tao-mockup-variants]');
let shopifyVariants = [];
try {
  shopifyVariants = JSON.parse(variantDataNode?.textContent || '[]');
} catch (error) {
  console.warn('TAO mockup variant data could not be parsed.', error);
}

/* Preserve Liquid's Shopify variant order within the existing collection groups. */
const liveThemeMap = new Map(shopifyVariants.map(variant => [
    variant.themeId || `variant-${variant.id}`, variant,
]));
const themeOrder = [...liveThemeMap.keys()];
const THEMES = shopifyVariants.length ? themeOrder.map(id => {
    const variant = liveThemeMap.get(id);
    const definition = THEME_DEFINITIONS.find(theme => theme.id === id);
    return {
        ...(definition || THEME_DEFINITIONS.find(theme => theme.id === DEFAULT_THEME)),
        id,
        name: variant.title || variant.options?.[0] || definition?.name || id,
        tier: definition?.tier,
        price: variant.price / 100,
        available: variant.available,
        variantId: variant.id,
        catalogImage: variant.image,
        catalogFallback: !definition,
        ...(!definition ? { staticHero: true } : {}),
    };
}) : THEME_DEFINITIONS;

document.documentElement.classList.add('rv-enabled');

/* ---------- color gallery (S17) --------------------------------- */
/* Monochrome leads while it is the new collection. The order here must match the
   theme_groups order in tao-mockup-color-prices.liquid (price nodes swap by index). */
const TIERS = [
  { id: 'monochrome', label: 'Monochrome Collection', tag: 'Limited edition. Two tones of one color.' },
  { id: 'core',       label: 'Core Collection',       tag: 'Clean. Timeless. Effortlessly modern.' },
  { id: 'signature',  label: 'Signature Collection',  tag: 'Elevated finishes with more personality.' },
  { id: 'luxe',       label: 'Luxe Collection',       tag: 'Statement finishes for people who notice everything.' },
];
const BADGES = {
  'sea-glass': 'Best Seller', 'midnight-blue': "Founder's Pick",
  'cappuccino-taupe': 'New', 'marine-layer': 'New', 'desert-sage': 'New',
};

/* left-edge color sampled from each colorway's bathroom scene — the problem
   band paints this so the image dissolves into it seamlessly */
const SCENE_BG = {
  'sea-glass': '#0b1314',
  'marbel': '#20242a',
  'glacier-blue': '#0a2b3c',
  'champagne': '#1b1711',
  'midnight-blue': '#070d1c',
  'pink': '#231b1b',
  'london-fog': '#2a2622',
  'lilac': '#161621',
  'eclipse': '#1e1a14',
  'rose-gold': '#191211',
  'matte-black': '#20252a',
  'metallic-teal': '#06181a',
  'evergreen': '#05130f',
};

/* every colorway has a live plume video: it dissolves in over its still
   once buffered, so the color/image crossfade never waits on the download */
const SCENE_VIDEO = PROBLEM_SCENE_VIDEO;

/* Every well stays light: the front renders carry baked-in drop shadows that
   were composited over white, and on a dark well those shadows read as grubby
   halos. So contrast is bought by dropping the pale finishes to a light tinted
   grey — enough to hold a white body, still bright enough for the shadow to
   sit on. The five dark finishes keep the porcelain, which already separates
   them completely. The luminance gap either side of .30 is wide (rose gold
   .36, arctic teal .09), so no colorway sits on the fence. */
const wellFor = (hex) => {
  const chan = (i) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const enc = (c) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);
  const lum = (v) => 0.2126 * lin(v[0]) + 0.7152 * lin(v[1]) + 0.0722 * lin(v[2]);
  const rgb = [chan(0), chan(1), chan(2)];
  if (lum(rgb) <= 0.30) return `color-mix(in srgb, ${hex} 16%, #f2f3f3)`;
  /* mute the hue so the well never competes with the body it holds, then land
     it on a fixed luminance so all eight pale cards read as one family */
  const grey = (rgb[0] + rgb[1] + rgb[2]) / 3;
  const muted = rgb.map((c) => c * 0.55 + grey * 0.45);
  const k = 0.46 / Math.max(lum(muted), 1e-4);
  const lit = muted.map((c) => enc(Math.min(1, lin(c) * k)));
  /* then half the remaining distance to white — the wells read as tinted
     paper rather than grey card, and the baked shadows have room to fall */
  const out = lit.map((c) => Math.round(((c + 1) / 2) * 255));
  return `rgb(${out.join(' ')})`;
};

const tierWrap = $('#tier-groups');
let syncEdges = null;
if (tierWrap) {
  for (const tier of TIERS) {
    const themes = THEMES.filter(t => t.tier === tier.id);
    if (!themes.length) continue;
    const head = document.createElement('div');
    head.className = 'cstrip-head';
    head.innerHTML = `
      <h3>${tier.label}</h3>
      <span class="cst-tag">${tier.tag}</span>`;
    tierWrap.appendChild(head);
    themes.forEach((t, i) => {
      const priceNode = document.querySelector(`[data-tao-color-price-map="cards"] [data-theme-id="${t.id}"]`);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cw-card';
      b.dataset.id = t.id;
      b.setAttribute('aria-pressed', 'false');
      b.setAttribute('aria-label', `${t.name} — try this colorway on`);
      /* Native portrait renders share a calibrated camera and tinted studio.
         Liquid supplies versioned URLs; future catalog colors retain a fallback. */
      const cardVariant = findShopifyVariant(t);
      const renderThumb = cardVariant?.filmstripImage;
      const fallbackThumb = t.assetPlates ? `${ASSET_BASE}tao-mockup-front-sm-${t.id}.webp?v=2` : `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--front--sm--${t.id}.webp`;
      const shopThumb = cardVariant && cardVariant.image;
      b.innerHTML = `
        <span class="cw-well" style="--cw-sw:${t.swatch};--cw-well:${wellFor(t.swatch)}"><img${renderThumb ? ' class="cw-renderimg"' : shopThumb ? ' class="cw-shopimg"' : ''} src="${renderThumb || shopThumb || fallbackThumb}" alt="" loading="lazy" decoding="async" width="600" height="660" onerror="this.classList.remove('cw-renderimg');this.classList.add('cw-shopimg');this.onerror=null;this.src='${shopThumb || fallbackThumb}'"></span>
        <span class="cw-name">${t.name}</span>
        ${BADGES[t.id] ? `<span class="cw-badge">${BADGES[t.id]}</span>` : ''}`;
      /* Move the Liquid node so section refreshes retain server/client price parity. */
      if (priceNode) b.appendChild(priceNode);
      b.addEventListener('click', () => {
        dispatchEvent(new CustomEvent('tao:set-theme', { detail: t.id }));
      });
      /* iOS: a tap that lands while the strip is still gliding (momentum,
         or the auto-centre scroll) stops the scroll and never becomes a
         click — pointerup catches it. Double-fire on normal taps is
         harmless: same-theme re-applies are no-ops upstream. */
      let px = 0, py = 0;
      b.addEventListener('pointerdown', (e) => { px = e.clientX; py = e.clientY; }, { passive: true });
      b.addEventListener('pointerup', (e) => {
        if (Math.abs(e.clientX - px) > 8 || Math.abs(e.clientY - py) > 8) return;
        dispatchEvent(new CustomEvent('tao:set-theme', { detail: t.id }));
      }, { passive: true });
      tierWrap.appendChild(b);
    });
  }
  $('#cs-arrow')?.addEventListener('click', () => {
    tierWrap.scrollBy({ left: tierWrap.clientWidth * 0.7, behavior: 'smooth' });
    setTimeout(syncEdges, 450);   /* scroll events can defer in occluded windows */
  });
  $('#cs-arrow-left')?.addEventListener('click', () => {
    tierWrap.scrollBy({ left: -tierWrap.clientWidth * 0.7, behavior: 'smooth' });
    setTimeout(syncEdges, 450);
  });
  /* edge fades + arrow only exist while there is somewhere to go */
  const wrap = tierWrap.closest('.cstrip-wrap');
  syncEdges = () => {
    wrap.classList.toggle('can-left', tierWrap.scrollLeft > 4);
    wrap.classList.toggle('can-right', tierWrap.scrollLeft < tierWrap.scrollWidth - tierWrap.clientWidth - 4);
  };
  tierWrap.addEventListener('scroll', syncEdges, { passive: true });
  addEventListener('resize', syncEdges);
  syncEdges();
}

/* ---------- showcase FLIP gallery (S17) ---------------------------
   Click a flanking shot and it trades places with the center hero:
   both tiles measure First, swap grid areas, Invert with transforms,
   then Play — one continuous flight, both directions at once. */
(() => {
  const show = $('.cshow');
  if (!show) return;
  const tiles = $$('.cs-g', show);
  let busy = false;
  /* re-request a wrapper's current picture at the other size. swapImg short-circuits when
     the url has not changed, so the record of it has to be cleared first. */
  const refit = (el, full) => {
    const wrap = el && el.querySelector('.xfade');
    if (!wrap) return;
    const url = wrap.dataset.url;
    if (!url) { wrap.dataset.full = full ? '1' : ''; return; }
    if ((wrap.dataset.full === '1') === !!full) return;
    wrap.dataset.full = full ? '1' : '';
    delete wrap.dataset.url;
    swapImg(wrap, url);
  };

  const swap = (tile) => {
    const big = tiles.find(x => x.dataset.slot === 'big');
    if (!tile || tile === big || busy) return;
    busy = true;
    const pair = [big, tile];
    const first = pair.map(x => x.getBoundingClientRect());
    const startR = pair.map(x => parseFloat(getComputedStyle(x).borderRadius) || 0);
    const from = tile.dataset.slot;
    big.dataset.slot = from;  big.style.gridArea = from;
    tile.dataset.slot = 'big'; tile.style.gridArea = 'big';
    /* The promoted tile is about to be drawn several times larger than it was measured
       for, so it stops being allowed the small cut and reloads at full size; the demoted
       one may have the small cut back. Without this a phone would put a 400px picture
       into a 346px slot at 3x and it would visibly soften on the tap. */
    refit(tile, true);
    refit(big, false);
    /* phones: slots differ too much in aspect — the scale(sx,sy) flight
       squishes the photos mid-air, so tiles just trade places cleanly */
    if (RM || matchMedia('(max-width: 560px)').matches) { busy = false; return; }
    const last = pair.map(x => x.getBoundingClientRect());
    const endR = pair.map(x => getComputedStyle(x).borderRadius);
    const anims = pair.map((x, i) => {
      const f = first[i], l = last[i];
      const sx = f.width / l.width, sy = f.height / l.height;
      x.style.zIndex = i === 0 ? 5 : 6;   /* the riser flies above */
      x.style.transformOrigin = 'top left';
      /* Web Animations: the flight expires onto true layout values — no
         inline styles to unwind at landing, so nothing can hitch there */
      return x.animate([
        { transform: `translate(${f.left - l.left}px, ${f.top - l.top}px) scale(${sx}, ${sy})`,
          borderRadius: `${startR[i] / sx}px / ${startR[i] / sy}px` },
        { transform: 'none', borderRadius: endR[i] },
      ], { duration: 680, easing: 'cubic-bezier(.33,.01,.18,1)' });
    });
    Promise.allSettled(anims.map(a => a.finished)).then(() => {
      pair.forEach((x) => { x.style.zIndex = x.style.transformOrigin = ''; });
      busy = false;
    });
    setTimeout(() => {   /* occluded windows may never resolve finished */
      if (!busy) return;
      anims.forEach(a => a.finish?.());
      pair.forEach((x) => { x.style.zIndex = x.style.transformOrigin = ''; });
      busy = false;
    }, 900);
  };
  show.addEventListener('click', (e) => swap(e.target.closest('.cs-g')));
  show.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); swap(e.target.closest('.cs-g')); }
  });
})();

/* ---------- theme sync ------------------------------------------ */
/* imagery that follows the colorway: two stacked layers crossfade on the
   same clock as the theme colors (mirrors the hero plate mechanism) */
/* ── phones get the small cut ──────────────────────────────────────
   A decoded image costs width x height x 4 bytes of bitmap whatever the file weighed,
   and iOS enforces a per-tab memory limit by killing the tab. Measured on this page at
   393x852: 126MB of bitmap held after one scroll to the bottom, and 78MB of that was
   pixels the phone can never show — the "Find your color" crops are 1000x760 sources
   rendered into 79x92 boxes, which is 2.6MB of memory each to draw a thumbnail.

   assets/<dir>/sm/ holds the same pictures cut for a phone. Wired here rather than at
   each call site because every themed image on this page already comes through this one
   function, so there is exactly one place to be right. If a small cut is missing the
   onerror falls back to the full one, so adding a new image cannot break the page — it
   just costs what it used to. */
/* Not every themed image may be cut down. The .cshow gallery lets you tap a flanking
   shot to trade it into the CENTRE slot — 570px on desktop, the full width on a phone —
   so a picture measured at 79px in its resting state can be asked to fill 346 a moment
   later. showcase/ IS that centre slot by default and must never be cut; detail/ and
   cshow/ can be, but only until they are promoted, which is what data-full marks. */
/* problem/ belongs here too, and finding out why is the lesson: I sized its cut from the
   DESKTOP box, where the bathroom scene is 827px in a column. On a phone it is nearly
   full-bleed at 378px, which at 3x wants 1134 — so a 480px cut that looked generous
   against the desktop measurement was less than half what the phone needed. Measure the
   box at the viewport you are cutting FOR. */
const NEVER_SMALL = /\/tao-mockup--(?:showcase|hero|whiter|problem)--/;
const SMALL_MEDIA = matchMedia('(max-width: 920px)');
const RAIL_MEDIA = matchMedia('(max-width: 560px)');   /* the colour shots ride a full-width rail here */
const smallCut = (url) => url.replace(/(\/tao-mockup--[^/]+?--)/, '$1sm--');
/* The Paige photographs are theme assets (…/assets/tao-mockup-paige-<id>-<slot>.jpg) with
   no hand-cut sm-- twin, and the double-dash rewrite above never matched them — so a phone
   was decoding 1000x750 and 1600x1200 sources into ~94px tiles. The CDN resizes any asset
   on the fly when _<width>x sits before the extension (verified live: /assets/name_400x.webp
   ?v=… returns the smaller picture), so the cut is asked for rather than shipped. 600 covers
   a 3x phone tile; the centre slot spans the phone's width and gets 900. Only the gallery
   tiles are cut this way: the box render is full-bleed on a phone and its 1200px source is
   already the right size — nothing is ever asked for larger than it is. */
const CDN_RESIZE = /(\/assets\/[^/?#]+?)(\.[a-z0-9]+)(?=[?#]|$)/i;
const cdnResize = (url, width) => url.replace(CDN_RESIZE, `$1_${width}x$2`);
/* The one place that decides which bytes a wrapper fetches. swapImg and the colourway
   prefetcher both come through here, so what is warmed ahead is what is later shown. */
function resolveImageCut(wrap, url) {
  if (!SMALL_MEDIA.matches || wrap.dataset.full === '1' || NEVER_SMALL.test(url)) return url;
  const tile = wrap.closest('.cs-g');
  if (tile && url.includes('/assets/')) return cdnResize(url, (tile.dataset.slot === 'big' || RAIL_MEDIA.matches) ? 900 : 600);
  return smallCut(url);
}

/* Each wrapper owns one cancellable incoming decode. Keep the painted image until
   its replacement can paint; obsolete loads must neither reveal nor retain bitmaps. */
const imageSwaps = new WeakMap();
function swapImg(wrap, url) {
    if (!wrap || wrap.dataset.url === url) return;
    imageSwaps.get(wrap)?.();
    const a = wrap.querySelector('.xa'), b = wrap.querySelector('.xb');
    if (!a || !b) return;
    wrap.dataset.url = url;
    const front = wrap.dataset.front === 'b' ? b : a;
    const back = front === a ? b : a;
    const pre = new Image();
    let cancelled = false;
    let revealed = false;
    let retireTimer;
    const cancel = () => {
        cancelled = true;
        clearTimeout(retireTimer);
        pre.onload = pre.onerror = null;
        pre.removeAttribute('src');
        if (!revealed && wrap.dataset.url === url) delete wrap.dataset.url;
        if (imageSwaps.get(wrap) === cancel) imageSwaps.delete(wrap);
    };
    imageSwaps.set(wrap, cancel);
    const fail = () => {
        if (cancelled) return;
        if (pre.src !== new URL(url, location.href).href) { pre.src = url; return; }
        delete wrap.dataset.url;
        cancel();
    };
    const reveal = async () => {
        try {
            await pre.decode();
            if (cancelled || wrap.dataset.url !== url) return;
            /* Retarget a rapid pick without exposing the half-faded old buffer. */
            front.style.transition = back.style.transition = 'none';
            front.style.opacity = '1';
            back.style.opacity = '0';
            back.getBoundingClientRect();
            front.style.transition = back.style.transition = '';
            back.src = pre.src;
            /* Decode the actual destination too: WebKit can discard a detached
               preloader's decoded backing before the DOM image is painted. */
            await back.decode();
            if (cancelled || wrap.dataset.url !== url) return;
            back.style.opacity = '1';
            front.style.opacity = '0';
            wrap.dataset.front = back === a ? 'a' : 'b';
            revealed = true;
            pre.onload = pre.onerror = null;
            pre.removeAttribute('src');
            retireTimer = setTimeout(() => {
                if (!cancelled && front.style.opacity === '0') front.removeAttribute('src');
                if (imageSwaps.get(wrap) === cancel) imageSwaps.delete(wrap);
            }, RM ? 0 : 800);
        } catch (error) { fail(); }
    };
    pre.decoding = 'async';
    pre.onload = reveal;
    pre.onerror = fail;
    pre.src = resolveImageCut(wrap, url);
}

/* problem-scene video: playback respects the user's pause toggle,
   viewport visibility, and tab visibility. play() is what starts the
   fetch (the element is preload="none", muted, playsinline), and the
   layer reveals on 'playing' so the fade never waits on the download. */
const sceneWrap = $('.problem-scene');
const sceneVid  = sceneWrap?.querySelector('.xv');
const sceneBtn  = $('#scene-pause');
let sceneInView = false;
let scenePausedByUser = false;
let scenePauseT = 0;

function syncScenePlayback() {
  if (!sceneWrap || !sceneVid) return;
  const active = !!sceneWrap.dataset.video;
  if (sceneBtn) sceneBtn.hidden = !active;
  if (!active) return;
  if (scenePausedByUser || !sceneInView || document.visibilityState === 'hidden') {
    sceneVid.pause();
  } else {
    sceneVid.play().catch(() => {});
    if (sceneVid.readyState >= 3) sceneVid.style.opacity = '1';
  }
}

/* The clip's bytes are only asked for once the stage is in view: assigning src is what
   some browsers take as the cue to open the stream even at preload="none", and a
   colourway switch made far above this section was paying for a clip nobody was
   looking at. The wanted clip waits in data-video until the observer says so. */
function syncSceneSource() {
  if (!sceneWrap || !sceneVid || !sceneInView) return;
  const vurl = sceneWrap.dataset.video;
  if (!vurl || sceneVid.getAttribute('src') === vurl) return;
  /* drop to the stills instantly while the new clip loads — the
     still crossfade underneath carries the transition */
  sceneVid.style.transition = 'none';
  sceneVid.style.opacity = '0';
  void sceneVid.offsetWidth;
  sceneVid.style.transition = '';
  sceneVid.src = vurl;
}

if (sceneVid) {
  sceneVid.addEventListener('playing', () => {
    if (sceneWrap.dataset.video) sceneVid.style.opacity = '1';
  });
  sceneVid.addEventListener('canplay', syncScenePlayback);
  sceneVid.addEventListener('tao:media-restored', syncScenePlayback);
  document.addEventListener('visibilitychange', syncScenePlayback);
  new IntersectionObserver((entries) => {
    sceneInView = entries.some(e => e.isIntersecting);
    syncSceneSource();
    syncScenePlayback();
  }, { threshold: 0.05 }).observe(sceneWrap);
}
sceneBtn?.addEventListener('click', () => {
  scenePausedByUser = !scenePausedByUser;
  sceneBtn.setAttribute('aria-label', scenePausedByUser ? 'Play the background animation' : 'Pause the background animation');
  sceneBtn.querySelector('use')?.setAttribute('href', scenePausedByUser ? '#ic-play' : '#ic-pause');
  syncScenePlayback();
});

function syncScene(t) {
  if (!sceneWrap) return;
  /* colorways without a rendered bathroom scene borrow the closest one (the still
     itself swaps with the other photographs — see listThemeImages) */
  const sid = t.sceneFrom || t.id;
  $('.problem')?.style.setProperty('--p-scene', SCENE_BG[sid] || '#0e1c1e');
  if (!sceneVid) return;
  clearTimeout(scenePauseT);
  const vurl = SCENE_VIDEO[sid];
  if (vurl && !RM && !checkSaveData()) {
    sceneWrap.dataset.video = vurl;
    syncSceneSource();
    syncScenePlayback();
  } else {
    delete sceneWrap.dataset.video;
    sceneVid.style.opacity = '0';
    if (sceneBtn) sceneBtn.hidden = true;
    /* pause only after the crossfade ends; cleared by any newer switch */
    scenePauseT = setTimeout(() => {
      if (!sceneWrap.dataset.video && !sceneVid.paused) sceneVid.pause();
    }, 800);
  }
}

const dockingPlayer = createDockingPlayer(document.querySelector('[data-tao-docking-video]'), {
    reducedMotion: RM,
    saveData: Boolean(navigator.connection?.saveData),
    mediaList: THEMES.map(theme => {
        const variant = findShopifyVariant(theme);
        return { id: theme.id, url: variant?.dockingVideo, poster: variant?.dockingPoster };
    }).filter(media => media.url && media.poster),
});
/* the docking stage itself, for the prepare wait below: the cover is only waited on
   while this box is near the viewport (the player's own warming is untouched) */
const dockingStage = document.querySelector('[data-tao-docking-video]')?.closest('.docking-media') || null;

/* ── the theme-following photographs, as one list ──────────────────────────
   Every picture a colourway swaps, paired with its wrapper, from one function: the
   commit (syncTheme) and the prefetcher (warmThemeAssets) read the same list, so what
   is warmed ahead is exactly what is shown. Wrappers inside a hidden section are left
   out — #finale-img sits in section.finale[hidden] and was fetched on every switch for
   a picture nothing ever painted. */
function listThemeImages(t) {
  const dockingVariant = findShopifyVariant(t);
  const sid = t.sceneFrom || t.id;
  const targets = [];
  const add = (wrap, url) => {
    if (wrap && url && !wrap.closest('section[hidden]')) targets.push({ wrap, url });
  };
  /* the Paige shoot where a photo exists for the slot, the original render where one does not */
  $$('.detail-img').forEach(img => add(img, paigeURL(t.id, img.dataset.crop)
    || `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--detail--${t.id}-${img.dataset.crop}.webp`));
  add($('#cs-main'), paigeURL(t.id, 'big')
    || `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--showcase--${t.id}.jpg`);
  /* The large gallery retains its styled photography. The included section
     uses the same calibrated product pair as the native filmstrip portraits. */
  add($('#cs-life'), paigeURL(t.id, 'lean')
    || `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--cshow--${t.id}-lean.webp`);
  add($('#box-img'), dockingVariant?.includedImage || dockingVariant?.heroImage
    || `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--gallery--${t.id}.webp`);
  add($('#finale-img'), `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--gallery--${t.id}.webp`);
  /* colorways without a rendered bathroom scene borrow the closest one */
  add(sceneWrap, `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--problem--bathroom-${sid}.jpg`);
  return targets;
}

/* Prepare only photographs near the reader, including on neighbour/intent warms.
   Keep three color records, and cancel their pending images on eviction. The HTTP
   cache retains compressed bytes for a later visit without retaining Image objects. */
const warmedThemes = new Map();
function loadThemePicture(src, warm) {
    return new Promise(done => {
        const image = new Image();
        let finished = false;
        const finish = () => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            image.onload = image.onerror = null;
            image.removeAttribute('src');
            warm.cancellations.delete(finish);
            done();
        };
        const timer = setTimeout(() => { warm.loads.delete(src); finish(); }, 8000);
        warm.cancellations.add(finish);
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.onload = () => image.decode().catch(() => { warm.loads.delete(src); }).then(finish);
        image.onerror = () => { warm.loads.delete(src); finish(); };
        image.src = src;
    });
}
function warmNearThemeAssets(id) {
    const theme = THEMES.find(item => item.id === id);
    if (!theme) return Promise.resolve();
    const key = `${id}|${SMALL_MEDIA.matches ? 'sm' : 'lg'}`;
    let warm = warmedThemes.get(key);
    if (!warm) warm = { loads: new Map(), cancellations: new Set() };
    warmedThemes.delete(key);
    warmedThemes.set(key, warm);
    while (warmedThemes.size > 3) {
        const oldest = warmedThemes.keys().next().value;
        warmedThemes.get(oldest).cancellations.forEach(cancel => cancel());
        warmedThemes.delete(oldest);
    }
    const urls = listThemeImages(theme)
        .filter(({ wrap }) => checkNearViewport(wrap, innerHeight))
        .map(({ wrap, url }) => resolveImageCut(wrap, url));
    const cover = findShopifyVariant(theme)?.dockingPoster;
    if (cover && dockingStage && checkNearViewport(dockingStage, innerHeight)) urls.push(cover);
    return Promise.all([...new Set(urls)].map(src => {
        if (!warm.loads.has(src)) warm.loads.set(src, loadThemePicture(src, warm));
        return warm.loads.get(src);
    }));
}
const warmThemeAssets = warmNearThemeAssets;
addEventListener('tao:theme-prepare', (e) => {
  const prep = e.detail;
  if (prep?.id && typeof prep.waitUntil === 'function') prep.waitUntil(warmNearThemeAssets(prep.id));
});
/* Ahead of the ask: the two neighbours in strip order (the arrow keys' next stops) at
   idle after every commit, and whichever colour a pointer or focus lands on. Both bow
   to Save-Data; the prepare wait above does not, since by then the pick is made. */
let neighbourWarm = 0;
addEventListener('tao:theme', (e) => {
  const id = e.detail?.id;
  if (!id || checkSaveData()) return;
  const i = THEMES.findIndex(t => t.id === id);
  if (i < 0 || THEMES.length < 2) return;
  /* never ahead of the page's own load — a warm that starts during boot
     holds the load event open on the neighbours' pictures */
  const warmNeighbours = () => {
    cancelIdle(neighbourWarm);
    neighbourWarm = idle(() => {
      /* Only the current viewport's photographs are candidates, for the selected
         color and its two neighbours. Far sections retain their last painted photo. */
      warmThemeAssets(id);
      warmThemeAssets(THEMES[(i + 1) % THEMES.length].id);
      warmThemeAssets(THEMES[(i - 1 + THEMES.length) % THEMES.length].id);
    }, 3000);
  };
  if (document.readyState === 'complete') warmNeighbours();
  else addEventListener('load', warmNeighbours, { once: true });
});
/* Intent needs a dwell: a pointer sweeping the sixteen swatches on its way to
   one would otherwise queue every colour's photographs ahead of the real pick.
   180 ms of rest on a card is a hover, not a pass. Focus counts at once. */
let intentTimer = 0;
function handleThemeIntent(e) {
  if (checkSaveData() || !(e.target instanceof Element)) return;
  const pick = e.target.closest('.cw-card[data-id], .sbp-item[data-id], .swatches .swatch[data-id]');
  clearTimeout(intentTimer);
  if (!pick) return;
  if (e.type === 'focusin') { warmThemeAssets(pick.dataset.id); return; }
  intentTimer = setTimeout(() => warmThemeAssets(pick.dataset.id), 180);
}
document.addEventListener('pointerover', handleThemeIntent, { passive: true });
document.addEventListener('pointerout', (e) => {
  if (e.target instanceof Element && e.target.closest('.cw-card[data-id], .sbp-item[data-id], .swatches .swatch[data-id]')) clearTimeout(intentTimer);
}, { passive: true });
document.addEventListener('focusin', handleThemeIntent);

/* Distant wrappers remember only the latest selection on EVERY change. Prepare
   them 1.5 viewports before entry and keep their existing image until decoded. */
const pendingSwaps = new Map();
const pendingIO = new IntersectionObserver((entries) => {
  for (const en of entries) {
    if (!en.isIntersecting) continue;
        // The horizontal rail clips its other photos out of intersection tests.
        // Prepare this bounded set together before a swipe can expose a cold frame.
        const gallery = en.target.closest('.cshow');
        const targets = gallery ? gallery.querySelectorAll('.xfade') : [en.target];
        targets.forEach(function prepareNearbyPhoto(wrap) {
            const url = pendingSwaps.get(wrap);
            pendingSwaps.delete(wrap);
            pendingIO.unobserve(wrap);
            if (url) swapImg(wrap, url);
        });
  }
}, { rootMargin: '150% 0px', threshold: 0 });
/* within `margin` px above or below the screen — half a screen for the boot deferral
   (as before), a whole screen for the prepare wait (warmNearThemeAssets) */
const checkNearViewport = (el, margin = innerHeight * 0.5) => {
  const r = el.getBoundingClientRect();
  return r.bottom > -margin && r.top < innerHeight + margin;
};
function applyThemeImage(wrap, url) {
    if (!checkNearViewport(wrap, innerHeight * 1.5)) {
        pendingSwaps.set(wrap, url);
        pendingIO.observe(wrap);
        imageSwaps.get(wrap)?.();
        return;
    }
    pendingSwaps.delete(wrap);
    pendingIO.unobserve(wrap);
    swapImg(wrap, url);
}

function syncTheme(t) {
  if (!t) return;
  const dockingVariant = findShopifyVariant(t);
  /* The docking swap measures its layer to arm the cross-fade, which would force
     a layout in the middle of the commit's task — after the palette write and
     before the frame's own style pass, so the page would pay that pass twice.
     One frame later it rides the pass the frame does anyway; the cover's fade
     still starts inside the same 16 ms window as the palette. */
  requestAnimationFrame(() => dockingPlayer.setMedia({ url: dockingVariant?.dockingVideo, poster: dockingVariant?.dockingPoster }));
  updateRenderedProductPrices(t);
  /* gallery selection */
  $$('.cw-card').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.id === t.id)));
  /* theme-following imagery, every wrapper on the commit's own tick */
  listThemeImages(t).forEach(({ wrap, url }) => applyThemeImage(wrap, url));
  /* keep the chosen card in view inside the strip (never scroll the page) */
  const sel = $('.cw-card[aria-pressed="true"]');
  if (sel && tierWrap) {
    const smooth = syncTheme.booted;
    syncTheme.booted = true;
    /* offsetLeft/clientWidth are layout reads: next frame, same reason as above */
    requestAnimationFrame(() => {
      const left = sel.offsetLeft - tierWrap.clientWidth / 2 + sel.clientWidth / 2;
      tierWrap.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
      if (typeof syncEdges === 'function') setTimeout(syncEdges, 450);
    });
  }
  $('.box-callouts')?.toggleAttribute('hidden', !dockingVariant?.includedImage);
  syncScene(t);
  /* sticky bar */
  const sw = $('#sb-swatch'), nm = $('#sb-name');
  if (sw) sw.style.setProperty('--sb-sw', t.swatch);
  if (nm) nm.textContent = t.name;

}

addEventListener('tao:theme', (e) => syncTheme(e.detail));
/* The visible selection, including a legacy color link, is authoritative after
   main.js boots. Storage is only a preference and may belong to a prior visit. */
const bootId = document.body.dataset.taoTheme || window.__bootTheme || DEFAULT_THEME;
let currentId = (THEMES.find(t => t.id === bootId) || THEMES[0]).id;
addEventListener('tao:theme', (e) => { if (e.detail?.id) currentId = e.detail.id; });
let renderedVariantSnapshot = createVariantSnapshot(document);
syncTheme(THEMES.find(t => t.id === currentId));

/* ---------- the iPhone's chrome follows the colorway ---------------------
   iOS Safari tints its TOP bar from meta[name=theme-color] (a fresh
   node applies instantly — measured; mutating content is what iOS
   drops). Follow the committed 'tao:theme' event so browser chrome
   never previews a selection while its hero is still loading.
   The sticky buy bar is painted entirely by CSS on the page's shared
   theme clock; overriding its background here makes it jump ahead. */
/* Scrolled away from the top, newer iOS ignores the meta and frosts the
   status strip from the pixels beneath it — the nav's translucent glass
   over content that is still mid-fade. No inline paint can reach a
   ::before, so a shim does the honest thing: a fixed strip exactly the
   status bar's height that takes the committed target color,
   holds while the real fade catches up underneath, then
   dissolves into the (by now correct) glass. Invisible at rest. */
const chromeShim = (() => {
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  Object.assign(el.style, {
    position: 'fixed', top: '0', left: '0', right: '0',
    height: 'env(safe-area-inset-top, 0px)',
    zIndex: '112', pointerEvents: 'none',
    opacity: '0', transition: 'opacity .45s ease',
  });
  document.body.appendChild(el);
  return el;
})();

const chromeTint = (t) => {
  if (!t) return;
  const color = t.bg2 || t.bg;
  if (!color) return;
  const paint = () => {
    const old = document.querySelector('meta[name="theme-color"]');
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = color;
    (old?.parentNode || document.head).insertBefore(meta, old || null);
    old?.remove();
    document.documentElement.style.backgroundColor = color;
  };
  paint();
  /* at the top of the page the strip shows the hero, not the nav glass —
     match what each state is about to become */
  const atTop = $('.nav')?.dataset.state !== 'opaque';
  chromeShim.style.transition = 'none';
  chromeShim.style.background = atTop ? (t.bg || color) : color;
  chromeShim.style.opacity = '1';
  void chromeShim.offsetWidth;
  chromeShim.style.transition = '';
  clearTimeout(chromeTint._settle);
  chromeTint._settle = setTimeout(() => {
    paint();
    chromeShim.style.opacity = '0';           /* dissolve into the real glass */
  }, 900);
};
/* a frame later: the shim reads computed styles, and a read inside the commit's
   task would force the page's style pass early (see syncTheme) */
addEventListener('tao:theme', (e) => requestAnimationFrame(() => chromeTint(e.detail)));
chromeTint(THEMES.find(t => t.id === currentId));

/* ---------- rotating retail box (specs accordion) ------------------
   Seven box renders exist from the classic line; every colorway maps
   to the nearest one and the loop swaps with the theme. */
(() => {
  const v = $('#pkg-vid');
  if (!v) return;
  const MAP = {
    'marbel': 'wht',
    'sea-glass': 'evg', 'evergreen': 'evg', 'metallic-teal': 'evg',
    'midnight-blue': 'mnb', 'eclipse': 'mnb', 'matte-black': 'mnb',
    'pink': 'pbl', 'lilac': 'pbl',
    'rose-gold': 'rs',
    'champagne': 'san', 'london-fog': 'san', 'champagne-pearl': 'san',
    'glacier-blue': 'wcs', 'watercolor-sky': 'wcs',
    'smoky-quartz': 'wht',
    'cappuccino-taupe': 'san', 'marine-layer': 'wcs', 'desert-sage': 'evg'
  };
  /* stacked twins, like the hero plates: the incoming colorway loads in
     the hidden element, seeks to the visible one's loop time (all seven
     clips are exactly 6.0s), then the pair crossfades — the box keeps
     rotating through the swap instead of snapping to a new angle (and
     the poster never flashes). */
  const b = $('#pkg-vid-b');
  const frame = v.closest('.pkg-frame');
  const motionOK = !RM && !navigator.connection?.saveData;
  /* the specs section ships hidden: nothing in it can be seen, so a colour switch must
     not write posters and sources into it — the wish is kept for the observer instead */
  const checkPkgHidden = () => Boolean((frame || v).closest('section[hidden]'));
  let front = v, back = b, want = null, animating = false, pkgInView = false, pkgGen = 0;
  const srcFor = (key) => PACKAGING_VIDEO[key];
  const posterFor = (key) => `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--packaging--${key}-poster.jpg?v=1`;
  /* Offscreen (and reduced-data) changes land on the correct poster
     without fetching or decoding a video the visitor cannot see. */
  const setStatic = (key, gen = pkgGen) => {
    if (gen !== pkgGen) return;
    want = null;
    animating = false;
    front.pause(); back.pause();
    front.dataset.pkg = key;
    front.preload = 'none';
    front.poster = posterFor(key);
    front.src = srcFor(key);
    front.style.opacity = '1';
    back.style.opacity = '0';
    back.removeAttribute('src');
    back.removeAttribute('data-pkg');
    back.load();
  };
  const settle = () => {
    if (!want || animating || !pkgInView || document.hidden || !motionOK) return;
    const request = want; want = null;
    const { key, gen } = request;
    if (gen !== pkgGen) return;
    if (front.dataset.pkg === key) {
      front.play().catch(() => {});
      return;
    }
    animating = true;
    back.dataset.pkg = key;
    back.poster = posterFor(key);
    back.src = srcFor(key);
    back.load();
    back.addEventListener('loadeddata', () => {
      if (gen !== pkgGen) { animating = false; settle(); return; }
      if (!pkgInView || document.hidden) {
        animating = false;
        setStatic(key, gen);
        return;
      }
      try { back.currentTime = front.currentTime % (back.duration || 6); } catch (e) {}
      back.play().catch(() => {});
      back.style.opacity = '1';
      front.style.opacity = '0';
      setTimeout(() => {
        if (gen !== pkgGen) { animating = false; settle(); return; }
        front.pause();
        [front, back] = [back, front];
        animating = false;
        settle();                    /* catch up if colors kept cycling */
      }, 720);                       /* just past --theme-fade (.7s) */
    }, { once: true });
    back.addEventListener('error', () => { if (gen === pkgGen) { animating = false; settle(); } }, { once: true });
  };
  const apply = (id) => {
    const key = MAP[id] || 'wht';
    const gen = ++pkgGen;
    if (checkPkgHidden()) { want = { key, gen }; return; }
    if (!pkgInView || !motionOK) setStatic(key, gen);
    else { want = { key, gen }; settle(); }
  };
  const resumeRestoredBox = (event) => {
    if (event.target === front && pkgInView && motionOK && !document.hidden && !animating) {
      front.play().catch(() => {});
    }
  };
  [v, b].forEach(video => {
    video.addEventListener('canplay', resumeRestoredBox);
    video.addEventListener('tao:media-restored', resumeRestoredBox);
  });
  apply(currentId);
  addEventListener('tao:theme', (e) => { if (e.detail?.id) apply(e.detail.id); });
  if (frame) {
    new IntersectionObserver((entries) => {
      pkgInView = entries.some((entry) => entry.isIntersecting);
      if (pkgInView && motionOK) { want = { key: MAP[currentId] || 'wht', gen: pkgGen }; settle(); }
      else { front.pause(); back.pause(); }
    }, { rootMargin: '160px 0px', threshold: 0 }).observe(frame);
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { front.pause(); back.pause(); }
    else if (pkgInView) { want = { key: MAP[currentId] || 'wht', gen: pkgGen }; settle(); }
  });
})();

/* feature-quad dark variants are stacked <video> twins now — their
   opacity crossfades via --featdark-op set in main.js scrimTo() */

/* Mockup CTAs elsewhere on the page delegate to the single Shopify-native,
   offer-aware product form in the hero. Commerce state stays owned by Horizon. */
document.addEventListener('click', function handleMockupAddToCart(event) {
  const trigger = event.target instanceof Element
    ? event.target.closest('[data-action="mockup-pdp:add-to-cart"]')
    : null;
  if (!(trigger instanceof HTMLButtonElement)) return;

  event.preventDefault();
  const buy = document.querySelector('.tao-native-buy');
  const nativeButton = buy?.querySelector('.add-to-cart-button');
  if (
    !(nativeButton instanceof HTMLButtonElement)
    || nativeButton.disabled
    || buy?.dataset.state === 'updating'
  ) return;

  nativeButton.click();
  syncMockupAddToCartState();
});

function syncMockupAddToCartLabels(preorderActive = Boolean(
  document.querySelector('[data-content="preorder-toggle-input"]')?.checked
)) {
  $$('[data-action="mockup-pdp:add-to-cart"] .atc-label').forEach((label) => {
    label.textContent = preorderActive ? 'Add to Preorder' : 'Add to Cart';
  });
}

function syncMockupAddToCartState() {
  const buy = document.querySelector('.tao-native-buy');
  const nativeButton = buy?.querySelector('.add-to-cart-button');
  const disabled = !(nativeButton instanceof HTMLButtonElement)
    || nativeButton.disabled
    || buy?.dataset.state === 'updating';

  $$('[data-action="mockup-pdp:add-to-cart"]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
  });
}

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.dataset.content === 'preorder-toggle-input') {
    syncMockupAddToCartLabels(event.target.checked);
  }
});

requestAnimationFrame(() => {
  syncMockupAddToCartLabels();
  syncMockupAddToCartState();
});

/* The protected native buy block renders preorder controls before its submit
   button. Measure the real button instead of assuming it is the first row so
   the custom icon, divider, and price remain aligned in every offer state. */
const observedNativeBuys = new WeakSet();
const nativeBuyResizeObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => {
    syncNativeBuyOverlay(entry.target.closest('.tao-native-buy') || entry.target);
  });
});

function syncNativeBuyOverlay(buy = document.querySelector('.tao-native-buy')) {
  if (!(buy instanceof HTMLElement)) return;

  const button = buy.querySelector('.add-to-cart-button');
  if (!(button instanceof HTMLElement)) return;

  const buyRect = buy.getBoundingClientRect();
  const buttonRect = button.getBoundingClientRect();
  const buttonCenter = buttonRect.top - buyRect.top + buttonRect.height / 2;
  buy.style.setProperty('--tao-atc-center', `${buttonCenter}px`);

  if (!observedNativeBuys.has(buy)) {
    observedNativeBuys.add(buy);
    nativeBuyResizeObserver.observe(buy);
    nativeBuyResizeObserver.observe(button);
  }
}

requestAnimationFrame(() => syncNativeBuyOverlay());
document.fonts?.ready?.then(() => syncNativeBuyOverlay());
window.addEventListener('resize', () => syncNativeBuyOverlay(), { passive: true });

function normalizeVariantLabel(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function findShopifyVariant(theme) {
  const exactMatch = shopifyVariants.find((candidate) => (
      candidate.themeId || `variant-${candidate.id}`
  ) === theme?.id);
  if (exactMatch) return exactMatch;

  const themeLabels = [theme?.id, theme?.name].map(normalizeVariantLabel).filter(Boolean);
  return shopifyVariants.find((candidate) => candidate.options.some((option) => {
    const normalizedOption = normalizeVariantLabel(option);
    return themeLabels.some((label) => normalizedOption.includes(label) || label.includes(normalizedOption));
  }));
}

function getOfferHandle(attributes) {
  const resolvedAttributes = attributes === undefined ? window.cartAttributes : attributes;
  if (resolvedAttributes && typeof resolvedAttributes === 'object') {
    return String(resolvedAttributes._offer_handle || '');
  }

  return String(window.cartAttributeOffer || '');
}

function getSelectedProductPriceWrappers(root = document) {
  return $$('[data-tao-price-location] > [data-content="product-prices"][data-item-id]', root);
}

function updateRenderedProductPrices(theme) {
  if (getOfferHandle()) return;

  const variant = findShopifyVariant(theme);
  if (!variant?.formattedPrice) return;

  getSelectedProductPriceWrappers().forEach((priceWrapper) => {
    priceWrapper.dataset.itemId = String(variant.id);
    $$('.price', priceWrapper).forEach((price) => {
      price.textContent = variant.formattedPrice;
    });
  });
}

/* ---------- whole-dollar prices stay whole -------------------------------
   Several hands write into this page's price wrappers — this module, the
   re-rendered section fragments, Horizon's variant lifecycle, the offer
   engine — and the last two format from raw cents, so a colour switch
   could flip $199 to $199.00 depending on who landed last. Rather than
   chase every writer, the wrappers are watched and any whole-dollar
   amount is shorn of its .00 the moment it appears. Fractional prices
   pass through untouched, and nothing outside these wrappers is read. */
(() => {
  const WHOLE = /\$(\d{1,3}(?:,\d{3})*)\.00(?!\d)/g;
  const shear = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      const trimmed = n.nodeValue.replace(WHOLE, '$$$1');
      if (trimmed !== n.nodeValue) n.nodeValue = trimmed;
    }
  };
  $$('[data-tao-price-location]').forEach((wrap) => {
    shear(wrap);
    new MutationObserver(() => shear(wrap))
      .observe(wrap, { subtree: true, childList: true, characterData: true });
  });
})();

let variantRequestController = null;
let pendingVariant = null;
let pendingOfferHandle = '';
let pendingCartObserver = null;
let currentVariantId = String(
  document.querySelector('.tao-native-buy input[name="id"]')?.value
  || new URL(window.location.href).searchParams.get('variant')
  || ''
);
let requestedVariantId = currentVariantId;
let renderedPreorderState = Boolean(
  document.querySelector('[data-content="preorder-toggle-input"]')?.checked
);
let renderedOfferHandle = getOfferHandle();
let activeVariantOfferHandle = renderedOfferHandle;
let activeVariantPreorderState = renderedPreorderState;

function getSectionId(element) {
  return element?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || '';
}

/* Keep only the last server-rendered commerce fragments. A failed request must
   restore the prior button, offer price and properties, not clone the optimistic
   DOM after its selected label and prices have already changed. */
function createVariantSnapshot(source) {
    const snapshot = document.implementation.createHTMLDocument('');
    const targets = [
        ...$$('.tao-native-buy', source),
        ...getSelectedProductPriceWrappers(source)
            .map(price => price.parentElement)
            .filter(wrapper => !wrapper.closest('.tao-native-buy')),
    ];

    for (const target of targets) {
        const sectionId = getSectionId(target);
        if (!sectionId) continue;
        let section = snapshot.getElementById(`shopify-section-${sectionId}`);
        if (!section) {
            section = snapshot.createElement('section');
            section.id = `shopify-section-${sectionId}`;
            snapshot.body.appendChild(section);
        }
        section.appendChild(target.cloneNode(true));
    }

    return snapshot;
}

/* Extract only our server-rendered commerce islands before parsing. A whole
   product DOM per color retained thousands of unrelated nodes and media URLs.
   Templates keep fallback markup inert during a rolling/cached deployment. */
function parseVariantFragments(html) {
    const snapshot = document.implementation.createHTMLDocument('');
    const template = document.createElement('template');
    const fragments = html.matchAll(/<!--tao-commerce-start:([^<>]+)-->([\s\S]*?)<!--tao-commerce-end-->/g);
    let count = 0;
    for (const [, sectionId, markup] of fragments) {
        let section = snapshot.getElementById(`shopify-section-${sectionId}`);
        if (!section) {
            section = snapshot.createElement('section');
            section.id = `shopify-section-${sectionId}`;
            snapshot.body.appendChild(section);
        }
        template.innerHTML = markup;
        section.appendChild(template.content);
        count++;
    }
    if (count) return snapshot;
    template.innerHTML = html;
    const fallback = createVariantSnapshot(template.content);
    template.content.replaceChildren();
    return fallback;
}

function updateVariantPriceFragments(source, sectionIds) {
    for (const sectionId of sectionIds) {
        const currentSection = document.getElementById(`shopify-section-${sectionId}`);
        const renderedSection = source.getElementById(`shopify-section-${sectionId}`);
        if (!currentSection || !renderedSection) continue;

        getSelectedProductPriceWrappers(currentSection).forEach(price => {
            const location = price.parentElement?.dataset.taoPriceLocation;
            if (!location) return;
            const renderedPrice = renderedSection.querySelector(
                `[data-tao-price-location="${location}"] > [data-content="product-prices"][data-item-id]`
            );
            if (renderedPrice) price.replaceWith(renderedPrice.cloneNode(true));
        });
    }
}

function checkNativeCartActive() {
    /* Horizon's ProductForm owns this loading marker. A disabled submit button
       alone also means sold out or a quantity limit, neither of which may block
       selecting another color. Keep this native integration hook localized. */
    return Boolean(document.querySelector('cart-items-component.cart-items-disabled'));
}

function clearPendingCartObserver() {
    pendingCartObserver?.disconnect();
    pendingCartObserver = null;
}

function refreshPendingVariant() {
    if (!pendingVariant || checkNativeCartActive()) return;
    const variant = pendingVariant;
    const offerHandle = pendingOfferHandle;
    pendingVariant = null;
    clearPendingCartObserver();
    refreshVariantFragments(variant, { offerHandle });
}

function observePendingCartCompletion() {
    if (pendingCartObserver) return;
    /* A network/cart error can unlock the native form without an offer-resolved
       event. Observe only native loading controls, only while a selection waits. */
    pendingCartObserver = new MutationObserver(refreshPendingVariant);
    for (const cartItems of $$('cart-items-component')) {
        pendingCartObserver.observe(cartItems, { attributes: true, attributeFilter: ['class'] });
    }
    const button = document.querySelector('.tao-native-buy .add-to-cart-button');
    if (button) {
        pendingCartObserver.observe(button, { attributes: true, attributeFilter: ['disabled'] });
    }
}

function updateVisibleVariantUrl(variantId) {
  const visibleUrl = new URL(window.location.href);
  visibleUrl.searchParams.set('variant', variantId);
  visibleUrl.searchParams.delete('color');
  visibleUrl.searchParams.delete('sections');
  window.history.replaceState({}, '', visibleUrl);
}

function commitRequestedVariant(productForm, variant) {
  const variantId = String(variant.id);
  const variantInput = productForm.querySelector('input[name="id"]');

  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = variantId;
    variantInput.setAttribute('value', variantId);
  }

  requestedVariantId = variantId;
  updateVisibleVariantUrl(variantId);
}

async function refreshVariantFragments(variant, options = {}) {
  const requestedOfferHandle = options.offerHandle ?? getOfferHandle();
  const sectionElements = [...getSelectedProductPriceWrappers(), ...$$('.tao-native-buy')]
    .map(element => element.closest('[id^="shopify-section-"]'))
    .filter(Boolean);
  const sectionIds = [...new Set(sectionElements.map(element => getSectionId(element)).filter(Boolean))];
  if (!sectionIds.length) return;

  variantRequestController?.abort();
  variantRequestController = new AbortController();
  const { signal } = variantRequestController;
  const buy = document.querySelector('.tao-native-buy');
  const productForm = buy?.querySelector('product-form-component');
  const variantEventTarget = productForm?.closest('.shopify-section');
  const productId = productForm?.dataset.productId || '';
  const previousVariant = shopifyVariants.find(candidate => String(candidate.id) === currentVariantId);
  if (!(productForm instanceof HTMLElement) || !(variantEventTarget instanceof HTMLElement)) return;

  /* The renderer commits visible selection before notifying this adapter.
     FormData for an existing cart request is already captured, so the visible
     variant's identity can commit now while its fragment refresh waits. */
  commitRequestedVariant(productForm, variant);
  activeVariantOfferHandle = requestedOfferHandle;
  activeVariantPreorderState = Boolean(
      document.querySelector('[data-content="preorder-toggle-input"]')?.checked
  );
  if (checkNativeCartActive()) {
      pendingVariant = variant;
      pendingOfferHandle = requestedOfferHandle;
      observePendingCartCompletion();
      return;
  }

  pendingVariant = null;
  clearPendingCartObserver();

  /* The selection is committed above; the server round trip and compact
     commerce-fragment parse and splice used to run on the same task as the
     colour commit. They now start a frame later, so the commit's own frame paints
     first. The form is locked only here, when the fetch is really about to go — a
     lock that waited on idle kept Add to Cart dead for most of a second — and a newer
     selection aborts this controller in the gap, so a burst of arrow presses fetches
     once, for the last colour. */
  await new Promise((resume) => requestAnimationFrame(() => setTimeout(resume, 0)));
  if (signal.aborted) return;
  buy?.setAttribute('data-state', 'updating');
  syncMockupAddToCartState();
  /* Reuse Horizon's native variant lifecycle so its existing product-form
     instance owns disabling, form values, preorder state, and offer UI. */
  variantEventTarget.dispatchEvent(new VariantSelectedEvent(variant));

  const requestUrl = new URL(window.location.href);
  requestUrl.searchParams.set('variant', variant.id);
  requestUrl.searchParams.delete('sections');

  try {
    const response = await fetch(`${requestUrl.pathname}${requestUrl.search}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal,
    });
    if (!response.ok) throw new Error(`Product rendering failed with HTTP ${response.status}`);

    const renderedProductDocument = parseVariantFragments(await response.text());
    if (signal.aborted) return;
    const renderedBuy = renderedProductDocument.querySelector('.tao-native-buy');
    if (!renderedBuy) throw new Error('Rendered native product form was not found.');
    const renderedForm = renderedBuy.querySelector('product-form-component');
    if (
        renderedForm?.dataset.productId !== productId
        || renderedForm.querySelector('input[name="id"]')?.value !== String(variant.id)
    ) {
        throw new Error('Rendered product selection did not match the requested variant.');
    }

    const renderedOriginalPrice = renderedBuy
      .querySelector('input[name="properties[_local_original_price]"]')
      ?.value;

    const nextVariantSnapshot = createVariantSnapshot(renderedProductDocument);
    updateVariantPriceFragments(renderedProductDocument, sectionIds);

    variantEventTarget.dispatchEvent(new VariantUpdateEvent(variant, 'tao-mockup-color-picker', {
      html: renderedProductDocument,
      productId,
    }));

    const variantInput = productForm.querySelector('input[name="id"]');
    variantInput?.setAttribute('value', String(variant.id));

    const originalPriceInput = productForm.querySelector('input[name="properties[_local_original_price]"]');
    if (originalPriceInput instanceof HTMLInputElement && renderedOriginalPrice) {
      originalPriceInput.value = renderedOriginalPrice;
      originalPriceInput.setAttribute('value', renderedOriginalPrice);
    }

    currentVariantId = String(variant.id);
    renderedVariantSnapshot = nextVariantSnapshot;
    renderedOfferHandle = requestedOfferHandle;
    renderedPreorderState = Boolean(
      document.querySelector('[data-content="preorder-toggle-input"]')?.checked
    );
  } catch (error) {
    if (!signal.aborted && error.name !== 'AbortError') {
      console.warn('TAO mockup variant refresh failed.', error);
      if (previousVariant) {
        commitRequestedVariant(productForm, previousVariant);
        updateVariantPriceFragments(renderedVariantSnapshot, sectionIds);
        variantEventTarget.dispatchEvent(new VariantUpdateEvent(previousVariant, 'tao-mockup-color-picker', {
          html: renderedVariantSnapshot,
          productId,
        }));
        document.dispatchEvent(new CustomEvent('tao:selection-restore', {
            detail: { themeId: previousVariant.themeId || `variant-${previousVariant.id}` },
        }));
      }
    }
  } finally {
    if (!signal.aborted) {
      document.querySelector('.tao-native-buy')?.removeAttribute('data-state');
      syncMockupAddToCartState();
      requestAnimationFrame(() => syncNativeBuyOverlay());
    }
  }
}

function updateShopifyVariant(theme) {
  const variant = findShopifyVariant(theme);

  if (!variant || requestedVariantId === String(variant.id)) return;
  refreshVariantFragments(variant);
}

/* Every picker, arrow and Play step commits through main.js. The palette event
   remains presentation-only; it must not initiate a second commerce request. */
function handleCommittedSelection(event) {
    if (event.detail?.themeId) updateShopifyVariant({ id: event.detail.themeId });
}

document.addEventListener('tao:selection', handleCommittedSelection);

function refreshOfferDependentProductFragments(event) {
  const cartAttributes = event.detail?.cart?.attributes ?? window.cartAttributes ?? {};
  const offerHandle = getOfferHandle(cartAttributes);
  const preorderState = cartAttributes._preorder === 'true';
  const shouldRefreshOffer = Boolean(offerHandle || renderedOfferHandle);
  syncMockupAddToCartLabels(preorderState);
  if (pendingVariant) pendingOfferHandle = offerHandle;

  /* ProductForm releases its submit lock on this same document event. Run
     after that listener so proxy controls mirror the final native state and
     any queued variant refresh immediately reacquires the native lock. */
  requestAnimationFrame(() => {
    syncMockupAddToCartState();

    if (pendingVariant) {
      refreshPendingVariant();
      return;
    }

    /* Native loading can clear before this frame and the pending observer may
       already have started the correct refresh. Do not abort it and fetch twice. */
    if (
        document.querySelector('.tao-native-buy')?.dataset.state === 'updating'
        && activeVariantOfferHandle === offerHandle
        && activeVariantPreorderState === preorderState
    ) return;

    if (!shouldRefreshOffer && preorderState === renderedPreorderState) return;

    const requestedVariant = shopifyVariants.find((variant) => String(variant.id) === requestedVariantId);
    if (requestedVariant) refreshVariantFragments(requestedVariant, { offerHandle });
  });
}

document.addEventListener('offer-engine:cart-resolved', refreshOfferDependentProductFragments);

const initialThemeId = document.body.dataset.taoTheme || window.__bootTheme;
if (initialThemeId) updateShopifyVariant({ id: initialThemeId });

/* sticky-bar swatch (or the color name) opens a colorway menu; picking
   a color applies it and closes the menu */
const sbSwatch = $('#sb-swatch');
const sbName = $('#sb-name');
const sbPop = $('#sb-pop');
if (sbSwatch && sbPop) {
  for (const tier of TIERS) {
    const themes = THEMES.filter(t => t.tier === tier.id);
    if (!themes.length) continue;
    const h = document.createElement('span');
    h.className = 'sbp-tier';
    h.textContent = tier.label;
    sbPop.appendChild(h);
    for (const t of themes) {
      const priceNode = document.querySelector(`[data-tao-color-price-map="sticky"] [data-theme-id="${t.id}"]`);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sbp-item';
      b.dataset.id = t.id;
      b.setAttribute('aria-pressed', String(t.id === currentId));
      b.innerHTML = `<span class="sbp-dot" style="--sw:${t.swatch}"></span><span class="sbp-name">${t.name}</span>`;
      if (priceNode) b.appendChild(priceNode);
      b.addEventListener('click', () => {
        dispatchEvent(new CustomEvent('tao:set-theme', { detail: t.id }));
        setOpen(false);
        sbSwatch.focus({ preventScroll: true });
      });
      sbPop.appendChild(b);
    }
  }
  const setOpen = (open) => {
    sbPop.classList.toggle('on', open);
    sbSwatch.setAttribute('aria-expanded', String(open));
    sbName?.setAttribute('aria-expanded', String(open));
    if (open) sbPop.querySelector('[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest' });
  };
  const toggle = () => setOpen(!sbPop.classList.contains('on'));
  sbSwatch.addEventListener('click', toggle);
  sbName?.addEventListener('click', toggle);
  /* arrows step one colorway at a time (menu can stay open to watch
     the selection ring move) */
  const sbPrev = $('#sb-prev'), sbNext = $('#sb-next');
  const stepTheme = (dir) => {
    const i = THEMES.findIndex(t => t.id === currentId);
    const next = THEMES[(i + dir + THEMES.length) % THEMES.length].id;
    currentId = next;   // optimistic: rapid clicks keep stepping in order
    dispatchEvent(new CustomEvent('tao:set-theme', { detail: next }));
  };
  sbPrev?.addEventListener('click', () => stepTheme(-1));
  sbNext?.addEventListener('click', () => stepTheme(1));
  document.addEventListener('click', (e) => {
    if (!sbPop.classList.contains('on')) return;
    if (sbSwatch.contains(e.target) || sbName?.contains(e.target) || sbPop.contains(e.target)
      || sbPrev?.contains(e.target) || sbNext?.contains(e.target)) return;
    setOpen(false);
  });
  /* close when keyboard focus leaves the widget */
  sbPop.addEventListener('focusout', (e) => {
    const to = e.relatedTarget;
    if (!to || sbPop.contains(to) || to === sbSwatch || to === sbName) return;
    setOpen(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !sbPop.classList.contains('on')) return;
    const within = sbPop.contains(document.activeElement)
      || document.activeElement === sbSwatch || document.activeElement === sbName;
    setOpen(false);
    if (within) sbSwatch.focus();   // never steal focus from elsewhere on the page
  });
  addEventListener('tao:theme', (e) => {
    const id = e.detail?.id;
    if (!id) return;
    $$('.sbp-item', sbPop).forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === id)));
  });
}

/* ---------- scroll reveals -------------------------------------- */
const revealed = new WeakSet();
const rvIO = new IntersectionObserver((entries) => {
  for (const en of entries) {
    if (!en.isIntersecting || revealed.has(en.target)) continue;
    revealed.add(en.target);
    en.target.classList.add('rv-in');
    rvIO.unobserve(en.target);
  }
}, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
/* anything already in view on load appears instantly (no animation) —
   reveals are for scrolling into content, not the first paint */
$$('[data-rv]').forEach(el => {
  const r = el.getBoundingClientRect();
  if (r.top < innerHeight && r.bottom > 0) {
    revealed.add(el);
    el.classList.add('rv-in', 'rv-noanim');
  }
});
$$('[data-rv]').forEach(el => rvIO.observe(el));

/* ---------- lab stat count-up ------------------------------------ */
/* 99.9 rolls up from zero the first time the stat scrolls into view */
const lsNum = $('.ls-num');
if (lsNum && !RM) {
  const node = lsNum.firstChild;
  const lsIO = new IntersectionObserver((ents) => {
    if (!ents.some(e => e.isIntersecting)) return;
    lsIO.disconnect();
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / 1600, 1);
      node.nodeValue = (99.9 * (1 - Math.pow(1 - p, 3))).toFixed(1);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, { threshold: 0.5 });
  lsIO.observe(lsNum);
}

/* ---------- feature badges: stills on phones ---------------------
   The alpha-video icons glitch on mobile (halo rings, rough loops,
   codec quirks per browser) — phones get their poster frames. */
if (matchMedia('(max-width: 920px)').matches) {
  /* Round 3: back to the poster frames on every phone — the alpha clips rendered with
     artefacts on Ryan's iPhone (round 2 had let the one-card rail play them). */
  $$('.feat-vid').forEach((v) => {
    const poster = v.getAttribute('poster');
    if (!poster) return;
    const im = new Image();
    im.src = poster;
    im.alt = '';
    im.className = v.className;
    v.replaceWith(im);
  });
} else {
  /* Desktop: four looping alpha videos autoplayed from parse to unload, on screen or
     not. They now run only while their strip is in view, and a visitor who asked for
     reduced motion or Save-Data keeps the poster frames — the sources come off so no
     bytes are fetched, and with no source the media-budget parker below leaves them be. */
  const feats = $$('.feat-vid');
  if (feats.length && (RM || checkSaveData())) {
    feats.forEach((v) => {
      v.removeAttribute('autoplay');
      v.pause();
      v.querySelectorAll('source').forEach(s => s.removeAttribute('src'));
      v.removeAttribute('src');
      try { v.load(); } catch (e) {}
    });
  } else if (feats.length) {
    const onScreen = new WeakSet();
    const syncFeat = (v) => {
      if (onScreen.has(v) && !document.hidden) v.play().catch(() => {});
      else v.pause();
    };
    feats.forEach((v) => {
      v.removeAttribute('autoplay');   /* the observer decides, not the parser */
      v.addEventListener('tao:media-restored', () => syncFeat(v));
    });
    const featIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) onScreen.add(en.target); else onScreen.delete(en.target);
        syncFeat(en.target);
      });
    }, { threshold: 0 });
    feats.forEach(v => featIO.observe(v));
    document.addEventListener('visibilitychange', () => feats.forEach(syncFeat));
  }
}

/* Ambient playback and source disposal share one owner. Restoring a source must
   happen before play(), and readiness must be able to retry an interrupted play. */
const ambientPlayers = $$('.ambient-loop').map(video => createAmbientVideo(video, {
    motionOK: !RM && !navigator.connection?.saveData,
}));
addEventListener('pagehide', event => {
    if (event.persisted) return;
    ambientPlayers.forEach(player => player.destroy());
    dockingPlayer.destroy();
}, { once: true });

/* ---------- media budget: let go of video that is nowhere near ----
   PAUSING A VIDEO DOES NOT FREE IT. A paused <video> that still has a src keeps its
   decoded frames, its buffered range and its whole media pipeline, and iOS counts all
   of that against a per-tab limit it enforces by killing the tab. Measured on this page
   at 393x852, scrolling once to the bottom left NINE videos live, three of them holding
   thirty to forty-four seconds of buffer — and every one of them stayed live after it
   had scrolled away, because pausing was as far as the existing observers went.

   So anything more than a couple of screens away gets its source taken off and its
   pipeline torn down with load(). The poster stays, so nothing looks empty and nothing
   moves; coming back within range puts the src back and the element resumes as before.
   The hero's canopy layers already did exactly this — this is the same idea applied to
   every other video on the page.

   Deliberately generous at two screens: close enough that a normal scroll never sees a
   poster where a loop should be, far enough that a page this tall is only ever holding
   the two or three clips around the reader.

   The teardown is the ONLY load() here. Measured on iOS 17: a <video> whose src is set
   fetches just its metadata, whatever preload says, but load() — even at preload
   "none" — buffers the whole clip, so an unpark puts the source back and leaves the
   fetch to the play() of whichever gate owns the clip. A clip that fed from <source>
   children comes back on the element's own src (the URL the browser had picked):
   re-writing a <source>'s src does not restart resource selection, and play() only
   restarts it from NETWORK_EMPTY. */
{
  const FAR = '200% 0px';
  const parked = new WeakMap();
  /* the hero runs its own budget, and so do the story rails (.vt-carousel):
     they keep only the active card and its neighbours live, and an unpark
     here would cut a rail clip off mid-play */
  const vids = $$('video').filter(v => !v.closest('.hero') && !v.closest('.vt-carousel') && !v.closest('.docking-media') && !v.matches('.ambient-loop'));
  const park = (v) => {
    if (parked.has(v)) return;
    /* never take the film the visitor is watching: running with the sound up means
       they pressed play and are listening, however far the page has scrolled */
    if (!v.paused && !v.muted) return;
    const src = v.currentSrc || v.getAttribute('src');
    if (!src) return;
    const sources = [...v.querySelectorAll('source')].map(sn => ({ n: sn, src: sn.getAttribute('src') }));
    parked.set(v, { src: v.getAttribute('src') || v.currentSrc, sources, t: v.currentTime, playing: !v.paused });
    try { v.pause(); } catch (e) {}
    v.removeAttribute('src');
    sources.forEach(({ n }) => n.removeAttribute('src'));
    try { v.load(); } catch (e) {}     /* the teardown; without it the buffer survives */
  };
  const unpark = (v) => {
    const st = parked.get(v);
    if (!st) return;
    parked.delete(v);
    // A color controller may have replaced the source while this video was parked.
    if (v.getAttribute('src')) return;
    st.sources.forEach(({ n, src }) => { if (src) n.setAttribute('src', src); });
    if (st.src) v.setAttribute('src', st.src);   /* no load(): see the note above */
    /* put it back where it was, so a loop does not visibly restart under the reader */
    if (st.t) v.addEventListener('loadedmetadata', () => { try { v.currentTime = st.t; } catch (e) {} }, { once: true });
    /* it was mid-loop when it went; it comes back mid-loop, not stopped on its poster */
    if (st.playing) v.play().catch(() => {});
    v.dispatchEvent(new CustomEvent('tao:media-restored'));
  };
  if (vids.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) unpark(e.target); else park(e.target); });
    }, { rootMargin: FAR, threshold: 0 });
    vids.forEach(v => io.observe(v));
  }
}

/* ---------- vibration counter (S10) ------------------------------ */
const vib = $('#vib-count');
if (vib && !RM) {
  const io = new IntersectionObserver((entries) => {
    if (!entries.some(e => e.isIntersecting)) return;
    io.disconnect();
    const T = 3000, target = 40000, t0 = performance.now();
    const word = $('#vib-word');
    word?.classList.add('on');   /* full-strength buzz for the whole count */
    const tick = (now) => {
      /* rAF timestamps can predate t0 by a frame — clamp so the first
         paint never shows a negative count */
      const p = Math.min(1, Math.max(0, (now - t0) / T));
      const eased = 1 - Math.pow(1 - p, 3);
      vib.textContent = Math.round(target * eased).toLocaleString('en-US');
      if (p < 1) requestAnimationFrame(tick);
      else word?.classList.remove('on');   /* stops dead at 40,000 */
    };
    requestAnimationFrame(tick);
  }, { threshold: 0.4 });
  io.observe(vib);
}

/* ---------- whitening wipe (S11) ---------------------------------
   Day 1 → day 14 as one sweep of light: --wh drives the clip edge,
   the seam, the floating day tag, and the timeline dot together.
   Runs only while on screen; restarts at day 1 on re-entry. */
const whProof = $('.wh-proof');
if (whProof && !RM) {
  const tag = $('.wh-daytag', whProof);
  const set = (p) => {
    whProof.style.setProperty('--wh', (p * 100).toFixed(2) + '%');
    const day = String(1 + Math.round(p * 13));
    if (tag && tag.dataset.day !== day) { tag.dataset.day = day; tag.textContent = 'Day ' + day; }
  };
  set(0);
  const H0 = 900, SW = 2800, H1 = 1800, T = H0 + SW + H1 + SW;
  const ease = (t) => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  let raf = 0, t0 = 0;
  const loop = (now) => {
    if (!t0) t0 = now;
    const t = (now - t0) % T;
    let p;
    if (t < H0) p = 0;
    else if (t < H0 + SW) p = ease((t - H0) / SW);
    else if (t < H0 + SW + H1) p = 1;
    else p = 1 - ease((t - H0 - SW - H1) / SW);
    set(p);
    raf = requestAnimationFrame(loop);
  };
  /* don't sweep until both frames can paint — with a cap, since decode()
     can stall indefinitely in occluded windows */
  const whReady = Promise.race([
    Promise.allSettled($$('.wh-stage img', whProof).map(i => i.decode())),
    new Promise(res => setTimeout(res, 1500)),
  ]);
  let whVis = false;
  const whIO = new IntersectionObserver(([en]) => {
    whVis = en.isIntersecting;
    if (whVis) {
      whReady.then(() => {
        if (whVis && !raf) { t0 = 0; set(0); raf = requestAnimationFrame(loop); }
      });
    } else if (raf) {
      cancelAnimationFrame(raf); raf = 0;
    }
  }, { threshold: 0.25 });
  whIO.observe(whProof);
}

/* ---------- community wall: rotating faces ------------------------
   All five tiles turn over together — the next five faces are preloaded
   and decoded, then cross-dissolved in one synchronized beat so the wall
   reads like a fresh row rather than a twitch. The pool is pre-ordered so
   every window of five has five distinct colorways and no adjacent
   look-alikes; the rotation walks that order from a random start, keeping
   each shown five varied. Only while on screen + tab visible; stilled for
   reduced motion. */
(() => {
  const wall = $('.comm-wall');
  /* phones: the faces ride an endless marquee — all ten tiles in one
     row, duplicated once so translateX(-50%) loops seamlessly. Tiles
     load eagerly (masked-off ones never intersect the viewport, so lazy
     ones would blink in dark as they scroll on). The desktop
     rotation-flip stays desktop-only. */
  if (matchMedia('(max-width: 760px)').matches) {
    if (wall) {
      $$('.comm-tile img', wall).forEach((i) => (i.loading = 'eager'));
      const setA = document.createElement('div');
      setA.className = 'comm-set';
      $$('.comm-tile', wall).forEach((t) => setA.appendChild(t));
      const setB = setA.cloneNode(true);
      setB.setAttribute('aria-hidden', 'true');
      const track = document.createElement('div');
      track.className = 'comm-marq';
      track.append(setA, setB);
      wall.textContent = '';
      wall.appendChild(track);
    }
    return;
  }
  if (!wall || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const tiles = $$('.comm-tile', wall);
  const N = tiles.length;
  /* the full 40-photo customer set, in order — the first N are the ones the
     markup already renders, so the walk below picks up at cw11 and loops */
  const POOL = [
    'cw01', 'cw02', 'cw03', 'cw04', 'cw05', 'cw06', 'cw07', 'cw08', 'cw09', 'cw10',
    'cw11', 'cw12', 'cw13', 'cw14', 'cw15', 'cw16', 'cw17', 'cw18', 'cw19', 'cw20',
    'cw21', 'cw22', 'cw23', 'cw24', 'cw25', 'cw26', 'cw27', 'cw28', 'cw29', 'cw30',
    'cw31', 'cw32', 'cw33', 'cw34', 'cw35', 'cw36', 'cw37', 'cw38', 'cw39', 'cw40',
  ];
  /* bare ids ('cw01'), the same key the pool uses, so the on-screen tiles really seed it */
  let shown = tiles.map(t => ((t.querySelector('img').getAttribute('src').split('/').pop() || '').split('?')[0].replace('.webp', '').match(/cw\d+$/) || [''])[0]);
  // POOL is the hand-curated order. Walk it straight through in pages of N,
  // starting right after the five tiles already on screen, so the wall flips
  // through the exact chosen sequence and loops. Skip any face still on screen
  // (only matters at the wrap seam).
  let pos = N;
  const dealNext = () => {
    const page = [];
    let guard = 0;
    while (page.length < N && guard < POOL.length * 2) {
      const id = POOL[pos % POOL.length];
      pos++; guard++;
      if (!page.includes(id) && !shown.includes(id)) page.push(id);
    }
    return page;
  };
  /* One <img> per face, made once and kept: the wall used to mint five fresh Image
     objects every two seconds and drop the old ones, so the same forty pictures were
     re-requested and re-decoded lap after lap. The tiles already on screen seed the
     pool; the page after the next is asked for a beat ahead, so its decode is done
     before the turnover that needs it. */
  const faceURL = (id) => `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--community--${id}.webp?v=10`;
  const faces = new Map();
  tiles.forEach((t, i) => {
    const im = t.querySelector('img');
    if (!im || !shown[i]) return;
    im.style.transition = 'opacity .5s ease';
    faces.set(shown[i], im);
  });
  const face = (id) => {
    let im = faces.get(id);
    if (im) return im;
    im = new Image();
    im.src = faceURL(id);
    im.alt = '';
    im.decoding = 'async';
    im.style.transition = 'opacity .5s ease';
    faces.set(id, im);
    return im;
  };
  const prime = () => { for (let k = 0; k <= N; k++) face(POOL[(pos + k) % POOL.length]); };
  let live = false, busy = false;
  const io = new IntersectionObserver((es) => {
    live = es.some(e => e.isIntersecting);
    if (live) prime();
  }, { threshold: 0.2 });
  io.observe(wall);
  const turnover = () => {
    if (!live || document.visibilityState !== 'visible' || busy) return;
    busy = true;
    const page = dealNext();
    const olds = tiles.map(t => t.querySelector('img'));      // capture current faces before layering
    const imgs = page.map((id) => {
      const im = face(id);
      im.style.opacity = '0';
      return im;
    });
    // Commit runs exactly once — whichever fires first: the decode finishing,
    // or a 1.2s safety cap. That cap is the fix for the "sometimes hangs" bug:
    // a stalled/slow image decode used to leave `busy` stuck true forever, so
    // every later tick bailed. Now the rotation always self-heals within one beat.
    let committed = false;
    const commit = () => {
      if (committed) return;
      committed = true;
      tiles.forEach((tile, i) => { if (imgs[i] && imgs[i].parentNode !== tile) tile.appendChild(imgs[i]); });
      requestAnimationFrame(() => requestAnimationFrame(() => { imgs.forEach(im => { im.style.opacity = '1'; }); }));
      setTimeout(() => {
        olds.forEach(old => { if (old && old.parentNode) old.remove(); });
        shown = page.slice();
        busy = false;
        prime();
      }, 600);
    };
    const ready = imgs.map(im => (im.decode ? im.decode().catch(() => {}) : new Promise(r => { im.onload = r; im.onerror = r; })));
    Promise.all(ready).then(commit);
    setTimeout(commit, 1200);
  };
  setInterval(turnover, 2000);
})();

/* ---------- off-screen animation pause -----------------------------
   The sonic rings, the shivering bristles, the spinning seal and the phone marquee
   are CSS animations that ran from load to unload wherever the reader was. One
   observer marks each stage is-offscreen as it leaves the viewport; the paused rule
   lives in the stylesheet. (After the wall block above, since the marquee is built
   there on phones.) */
{
  const stages = $$('.sonic-stage, .brushhead-bristles, .promise-seal, .comm-marq');
  if (stages.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => en.target.classList.toggle('is-offscreen', !en.isIntersecting));
    }, { rootMargin: '80px 0px', threshold: 0 });
    stages.forEach(el => io.observe(el));
  }
}

/* ---------- far sections: no transitions, no loops ----------------
   The colourway fade costs what the page re-styles every frame, and the sections
   a reader cannot see should cost nothing. Every section block more than half a
   screen from the viewport carries .is-far (pdp.css: its transitions run at 0 s
   and its animations pause) and loses it, resuming normally, before anything in
   it can be seen: the scroll reveals fire on entering the viewport itself, and
   both observers deliver in the same rendering update, ahead of the style pass
   that starts the reveal's transition. Half a screen rather than more because
   the tallest sections (the 7,800 px problem walk) count as near from the moment
   their top edge is within the margin. The header and the sticky bar are fixed,
   so they are always near. */
{
  const blocks = $$('#MainContent > * > *, footer.footer');
  if (blocks.length) {
    const io = new IntersectionObserver((entries) => {
      /* Measure before writing: auto containment can discard distant paint tiles
         without collapsing the page or removing content from find/accessibility. */
      const sizes = entries.map(en => {
        if (en.isIntersecting || en.target.tagName !== 'SECTION') return null;
        const style = getComputedStyle(en.target);
        const inset = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
          .reduce((sum, key) => sum + (parseFloat(style[key]) || 0), 0);
        return Math.max(0, en.boundingClientRect.height - inset);
      });
      entries.forEach((en, i) => {
        if (sizes[i] !== null) {
          en.target.style.containIntrinsicBlockSize = `auto ${sizes[i]}px`;
        }
        en.target.classList.toggle('is-far', !en.isIntersecting);
      });
    }, { rootMargin: '50% 0px', threshold: 0 });
    blocks.forEach(el => io.observe(el));
  }
}

/* ---------- pathogen carousel ------------------------------------
   Two stacked pages; switching fires a direction-aware fly-through —
   the leaving page's cards blur out one way, the arriving page's spring
   in from the other, staggered. Arrows wrap so both stay live. The
   whole thing only animates once the board is on screen. */
(() => {
  const car = $('.lab-carousel');
  if (!car) return;
  const pages = $$('.lp-page', car);
  const dots = $$('.lp-dot');
  const n = pages.length;
  let cur = 0;
  const go = (i, dir) => {
    cur = (i % n + n) % n;
    if (dir) car.dataset.dir = dir;   // 'next' | 'prev' — steers the fly direction
    car.dataset.page = cur;
    pages.forEach((p, k) => {
      const wasActive = p.classList.contains('is-active');
      p.classList.remove('is-leaving');
      if (k === cur) {
        p.classList.add('is-active');
      } else {
        if (wasActive) p.classList.add('is-leaving');   // run the exit once
        p.classList.remove('is-active');
      }
      p.setAttribute('aria-hidden', String(k !== cur));
    });
    dots.forEach((d, k) => {
      d.classList.toggle('is-on', k === cur);
      d.setAttribute('aria-selected', String(k === cur));
    });
  };
  /* the fly-through and the check-draw only fire once the board is on screen */
  const io = new IntersectionObserver((ents) => {
    if (ents.some(e => e.isIntersecting)) { car.classList.add('in-view'); io.disconnect(); }
  }, { threshold: 0.35 });
  io.observe(car);
  $('.lp-next', car)?.addEventListener('click', () => go(cur + 1, 'next'));
  $('.lp-prev', car)?.addEventListener('click', () => go(cur - 1, 'prev'));
  dots.forEach((d, k) => d.addEventListener('click', () => go(k, k > cur ? 'next' : 'prev')));
  go(0);
})();

/* phones: the paged pathogen board flattens into one plain row — the
   stacked .lp-page containers could still overlap on iOS, so they're
   removed outright and the eight cards become direct track children */
if (matchMedia('(max-width: 620px)').matches) {
  const track = $('.lab-carousel .lp-track');
  if (track) {
    const items = $$('.lp-item', track);
    track.textContent = '';
    items.forEach((i) => track.appendChild(i));
  }
}

/* Petri motion runs only inside visible dishes, including horizontal rail clipping. */
function initPetriDishes() {
    const dishes = document.querySelectorAll('[data-petri-dish]');
    if (!dishes.length || !('IntersectionObserver' in window)) return;
    const visibleDishes = new Set();

    function updatePetriMotion(dish) {
        dish.dataset.petriActive = String(visibleDishes.has(dish) && !document.hidden);
    }

    function handlePetriIntersections(entries) {
        entries.forEach(function updatePetriVisibility(entry) {
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
                visibleDishes.add(entry.target);
            } else {
                visibleDishes.delete(entry.target);
            }
            updatePetriMotion(entry.target);
        });
    }

    function handlePetriVisibility() {
        dishes.forEach(updatePetriMotion);
    }

    const observer = new IntersectionObserver(handlePetriIntersections, { threshold: 0 });
    dishes.forEach(function observePetriDish(dish) { observer.observe(dish); });
    document.addEventListener('visibilitychange', handlePetriVisibility);
}
initPetriDishes();

/* ---------- brushing mode switch (sonic bristles) ------------------
   Clean vs Gentle re-tunes the bristle shiver physics live. */
(() => {
  const sw = $('.mode-switch');
  const brl = $('.brushhead-bristles');
  if (!sw || !brl) return;
  /* Ryan's tuned physics. A 15ms clean cycle is under one frame at 60Hz and a 35ms
     gentle cycle at 1.1px is two — the panel samples them at an arbitrary phase, so
     gentle read as "barely moves" on a phone. Phones get a period they can resolve
     (>= 3 samples a cycle) and a wider throw, since the stage is smaller there. These
     are written as inline vars, which beat the matching media query in css/site.css —
     so the table has to know about the breakpoint too. */
  const MODES_WIDE = {
    clean:  { speed: 0.015, x: 2.3, y: 0,   blur: 0.8, glow: 0 },
    gentle: { speed: 0.035, x: 1.1, y: 0,   blur: 0.8, glow: 0 },
  };
  /* Periods chosen to walk their phase rather than strobe: 62ms is 3.72 frames at 60Hz
     and 7.44 at 120Hz, 95ms is 5.70 / 11.40. A near-integer multiple of the frame
     interval samples the same phases every cycle and the throw all but disappears. */
  const MODES_SMALL = {
    clean:  { speed: 0.062, x: 3.6, y: 0,   blur: 0.9, glow: 0 },
    gentle: { speed: 0.125, x: 1.2, y: 0,   blur: 0.5, glow: 0 },   /* round 4: read the difference at a glance */
  };
  const SMALL = matchMedia('(max-width: 920px)');
  const MODES = () => (SMALL.matches ? MODES_SMALL : MODES_WIDE);
  const UNIT = { speed: 's', x: 'px', y: 'px', blur: 'px', glow: 'px' };
  const applyPhysics = (mode) => {
    const m = MODES()[mode];
    for (const k in m) brl.style.setProperty(`--vpm-${k}`, m[k] + UNIT[k]);
    return m;
  };
  const syncLoadedBristlePhysics = () => {
    const decoded = typeof brl.decode === 'function' ? brl.decode() : Promise.resolve();
    decoded.catch(() => {}).then(() => {
      requestAnimationFrame(() => applyPhysics(sw.dataset.on || 'clean'));
    });
  };
  if (brl.complete && brl.naturalWidth > 0) syncLoadedBristlePhysics();
  else brl.addEventListener('load', syncLoadedBristlePhysics, { once: true });
  /* rotating a phone crosses the breakpoint with the inline vars already written */
  SMALL.addEventListener('change', () => {
    if (brl.style.getPropertyValue('--vpm-speed')) applyPhysics(sw.dataset.on || 'clean');
  });
  /* retime the running ripple animations in place — playbackRate keeps
     each ring's position, so the waves never reset or stutter */
  const setWaves = (mode) => {
    const stage = $('.sonic-stage');
    if (!stage) return;
    stage.dataset.brushmode = mode;
    const rate = mode === 'clean' ? 2 : 1;
    $$('.ring', stage).forEach(r => r.getAnimations().forEach(a => { a.playbackRate = rate; }));
  };
  setWaves('clean');
  sw.addEventListener('click', (e) => {
    const btn = e.target.closest('.ms-opt');
    if (!btn || btn.classList.contains('is-on')) return;
    sw.dataset.on = btn.dataset.mode;
    $$('.ms-opt', sw).forEach(b => {
      const on = b === btn;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', String(on));
    });
    const m = applyPhysics(btn.dataset.mode);
    setWaves(btn.dataset.mode);
    /* the first fact tells each mode's truth */
    const fx = $('#fact-fx');
    if (fx) fx.textContent = btn.dataset.mode === 'gentle'
      ? 'Gentle enough to help prevent bleeding gums'
      : '10× more effective between teeth';
    /* the copy tells each mode's story — crossfade the stacked states */
    const states = $('.sonic-states');
    if (states) {
      states.dataset.mode = btn.dataset.mode;
      $$('.sc-state', states).forEach((s) =>
        s.setAttribute('aria-hidden', String(!s.classList.contains('sc-' + btn.dataset.mode))));
    }
  });
})();

/* ---------- concern tabs (S13) ----------------------------------- */
const tabs = $$('.tab');
/* ← → stay reserved for the colorway switcher page-wide, so the tabs
   deliberately DON'T implement arrow-key navigation: every tab stays
   tabbable (no roving tabindex) and Home/End still jump the row */
function selectTab(tab) {
  for (const t of tabs) {
    const on = t === tab;
    t.setAttribute('aria-selected', String(on));
    const panel = document.getElementById(t.getAttribute('aria-controls'));
    if (panel) panel.hidden = !on;
  }
}
tabs.forEach((tab) => {
  tab.addEventListener('click', (e) => {
    selectTab(tab);
    /* phones: a mid-panel tab switch pins the tabs under the nav and
       drops you at the NEW panel's heading — not the section top, so
       switching Stains→Plaque lands on "Stop plaque…", not the eyebrow */
    if (matchMedia('(max-width: 880px)').matches) {
      const wrap = document.querySelector('.fix-tabs');
      if (wrap) {
        const navH = document.querySelector('.nav')?.getBoundingClientRect().height || 62;
        const top = wrap.getBoundingClientRect().top + scrollY - navH;   /* sticky tablist top */
        if (scrollY > top) scrollTo({ top, behavior: 'instant' });
      }
    }
    if (e.detail) tab.blur();   /* mouse click: drop focus so arrows keep driving colors */
  });
  tab.addEventListener('keydown', (e) => {
    let j = null;
    if (e.key === 'Home') j = 0;
    if (e.key === 'End')  j = tabs.length - 1;
    if (j === null) return;
    e.preventDefault();
    tabs[j].focus();
    selectTab(tabs[j]);
  });
});

/* ---------- brush-head variant swatches (S21) --------------------- */
$$('.hp-swatches').forEach((g) => {
  const card = g.closest('.hp-card');
  const img = card?.querySelector('.hp-img img');
  g.addEventListener('click', (e) => {
    const b = e.target.closest('.hp-sw');
    if (!b || !img) return;

    img.src = b.dataset.img;
    const variantInput = card.querySelector('input[name="id"]');
    const sellingPlanInput = card.querySelector('[data-head-selling-plan]');
    const submitButton = card.querySelector('.hp-subscribe-button');
    if (variantInput && b.dataset.variantId) variantInput.value = b.dataset.variantId;
    if (sellingPlanInput) sellingPlanInput.value = b.dataset.sellingPlanId || '';
    if (submitButton) submitButton.disabled = !b.dataset.variantId || !b.dataset.sellingPlanId;
    $$('.hp-sw', g).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
  });
});


/* ---------- gentle copy auto-fit --------------------------------
   The gentle state overlays inside the clean-defined box; wrap points
   differ per viewport, so fit is enforced by measurement, not faith. */
const fitGentle = () => {
  const g = $('.sc-gentle');
  const body = g?.querySelector('.body');
  if (!g || !body) return;
  body.style.fontSize = '';
  let size = parseFloat(getComputedStyle(body).fontSize);
  let guard = 10;
  while (guard-- && g.scrollHeight > g.clientHeight + 1 && size > 11.5) {
    size -= 0.5;
    body.style.fontSize = size + 'px';
  }
};
addEventListener('resize', fitGentle);
document.fonts?.ready?.then(fitGentle);
fitGentle();

/* ---------- reviews: filter + expander (S20) ---------------------- */
const chips = $$('.rev-filters .chip');
const cards = $$('.rev-card');
let expanded = false;
function applyFilter(topic) {
  cards.forEach(card => {
    const match = topic === 'all' || card.dataset.topic === topic;
    const collapsed = topic === 'all' && !expanded && card.classList.contains('rev-extra');
    card.hidden = !match || collapsed;
  });
  const more = $('#rev-more');
  if (more) more.hidden = false;   /* round 4: the button opens the Junip modal, so it stays under every filter */
}
/* every filter switch re-animates ALL visible cards in a staggered
   wave — otherwise the cards already scroll-revealed (rv-in) sit static
   while only the freshly-shown ones animate, so a filter looked half-alive */
function animateFilteredCards() {
  cards.filter(c => !c.hidden).forEach((c, i) => {
    c.style.setProperty('--fi', i);
    c.classList.remove('rev-flip');
    void c.offsetWidth;              // reflow so the animation restarts
    c.classList.add('rev-flip');
  });
}
chips.forEach(chip => chip.addEventListener('click', () => {
  chips.forEach(c => c.setAttribute('aria-pressed', String(c === chip)));
  applyFilter(chip.dataset.topic);
  animateFilteredCards();
}));
$('#rev-more')?.addEventListener('click', () => {
  expanded = true;
  applyFilter('all');
  /* the button hides itself — hand focus to the first newly revealed card */
  const first = $('.rev-extra');
  if (first) { first.tabIndex = -1; first.focus({ preventScroll: false }); }
});

/* ---------- sticky add-to-cart bar -------------------------------- */
const bar = $('#stickybar');
const sentinel = $('#swatches');
if (bar && sentinel) {
  const footer = $('#footer');
  const header = document.querySelector('[data-tao-mockup-header]');
  const stickyHeaderHeight = header?.getBoundingClientRect().height || 0;
  let pastSentinel = false;
  let footerVisible = false;

  const updateStickyBar = () => {
    const show = pastSentinel && !footerVisible;
    bar.classList.toggle('on', show);
    bar.setAttribute('aria-hidden', String(!show));
    if (!show) {
      $('#sb-pop')?.classList.remove('on');
      $('#sb-swatch')?.setAttribute('aria-expanded', 'false');
    }
  };

  const io = new IntersectionObserver((entries) => {
    for (const en of entries) {
      pastSentinel = !en.isIntersecting && en.boundingClientRect.bottom <= stickyHeaderHeight;
      updateStickyBar();
    }
  }, { rootMargin: `-${stickyHeaderHeight}px 0px 0px`, threshold: 0 });
  io.observe(sentinel);

  if (footer) {
    const footerIO = new IntersectionObserver((entries) => {
      footerVisible = entries.some((entry) => entry.isIntersecting);
      updateStickyBar();
    }, { threshold: 0 });
    footerIO.observe(footer);
  }

  /* A colourway switch can resize the hero, and a layout shift moves the
     sentinel without any scroll — the observers keep whatever answer they
     last computed and the bar can stay hidden on a page the visitor is
     halfway down. Re-derive both answers from live geometry whenever the
     theme changes; the observers stay in charge of scroll. */
  addEventListener('tao:theme', () => {
    requestAnimationFrame(() => {
      const s = sentinel.getBoundingClientRect();
      pastSentinel = s.bottom <= stickyHeaderHeight;
      if (footer) {
        const f = footer.getBoundingClientRect();
        footerVisible = f.top < innerHeight && f.bottom > 0;
      }
      updateStickyBar();
    });
  });

  /* rotating microcopy (Appendix B) */
  const LINES = [
    '<svg viewBox="0 0 24 24"><use href="#ic-shield"/></svg><span>Lifetime Warranty</span><i class="sep">·</i><svg viewBox="0 0 24 24"><use href="#ic-calendar"/></svg><span>45-Day Money-Back Guarantee</span>',
    '<svg viewBox="0 0 24 24"><use href="#ic-sparkle"/></svg><span>Always Clean. Always Ready.</span>',
    '<svg viewBox="0 0 24 24"><use href="#ic-sunrise"/></svg><span>Brush Better. Starting Tomorrow Morning.</span>',
  ];
  const rot = $('#sb-rotate');
  if (rot && !RM) {
    let li = 0;
    setInterval(() => {
      rot.classList.add('fade');
      setTimeout(() => {
        li = (li + 1) % LINES.length;
        rot.innerHTML = LINES[li];
        rot.classList.remove('fade');
      }, 500);
    }, 6000);
  }

}

/* ---------- email capture (S31, mock) ------------------------------ */
const form = $('#signup-form');
form?.addEventListener('submit', (e) => {
  e.preventDefault();
  const email = $('#signup-email');
  const err = $('#signup-error');
  if (!email.value || !/.+@.+\..+/.test(email.value)) {
    email.focus();
    email.setAttribute('aria-invalid', 'true');
    if (err) err.hidden = false;
    return;
  }
  if (err) err.hidden = true;
  /* keep the form on screen — just confirm and reset */
  email.value = '';
  const done = $('#signup-done');
  if (done) { done.hidden = false; done.focus(); }
});
$('#signup-email')?.addEventListener('input', (e) => {
  e.target.removeAttribute('aria-invalid');
  const err = $('#signup-error');
  if (err) err.hidden = true;
});

/* ---------- video stories carousel (S19c) -----------------------
   Centered coverflow of muted UGC testimonials (captions are burned into
   the source clips). Infinite: the card set is cloned either side and the
   track snaps back a full period (identical pixels) whenever the active
   index drifts out of the middle copy. Only the active card plays; when
   its clip ends the carousel advances. Tapping the active card pauses and
   resumes it. Mute state is shared — unmuting one unmutes them all. The
   rail autoplays silent, comes up with sound on the visitor's first step,
   tap or swipe, and then honours their mute button for the whole visit.
   Arrows or clicking a side card recentres. Plays only while on screen.
   One machine, every rail: the dentist strip (S6) rides the same code.
   Every one of its cards is a .vt-vid — phone clips and spoken
   testimonials (a still portrait whose narration is the audio track) —
   and their VTT captions render in the shared glass pill. Clones are
   full citizens: every control below binds per card, copies included, so
   a glide can land on one and keep playing. Only ids are stripped. The
   fold back into the middle copy never happens in front of the viewer —
   it rides the start of the next step, or an off-screen moment. */
/* The visitor's own mute/unmute, remembered for the whole page visit and
   shared by every rail: null until they press a mute button. */
let railSoundChoice = null;

/* WebKit can clear its native poster during a restored seek before it paints a
   video frame. Keep the authored poster beneath the video, and expose video pixels
   only after the compositor receives a frame for this source generation. */
function createTestimonialFrameCover(video) {
    const media = video.closest('.vt-media');
    let generation = 0;
    let callback = null;
    const reset = () => {
        generation++;
        if (callback !== null) video.cancelVideoFrameCallback?.(callback);
        callback = null;
        if (media && video.poster) {
            media.style.backgroundImage = `url(${JSON.stringify(video.poster)})`;
            video.style.opacity = '0';
        }
    };
    const arm = () => {
        reset();
        const expected = generation;
        if (video.requestVideoFrameCallback) {
            callback = video.requestVideoFrameCallback(() => {
                callback = null;
                if (expected === generation && video.getAttribute('src') && video.readyState >= 2) {
                    video.style.opacity = '1';
                }
            });
        }
    };
    if (!video.requestVideoFrameCallback) {
        video.addEventListener('loadeddata', () => {
            const expected = generation;
            requestAnimationFrame(() => requestAnimationFrame(() => {
                if (expected === generation && video.getAttribute('src') && video.readyState >= 2) {
                    video.style.opacity = '1';
                }
            }));
        });
    }
    return { arm, reset };
}

$$('.vt-carousel').forEach((car) => {
  const track = $('.vt-track', car);
  const viewport = $('.vt-viewport', car);
  if (!track || !viewport) return;
  const originals = $$('.vt-card', track);
  if (!originals.length) return;
  const N = originals.length;
  const dub = (c) => {
    const k = c.cloneNode(true);
    k.querySelectorAll('[id]').forEach(n => n.removeAttribute('id'));
    return k;
  };
  originals.map(dub).reverse().forEach(c => track.prepend(c));
  originals.map(dub).forEach(c => track.append(c));
  const cards = $$('.vt-card', track);
  const vids = cards.map(c => $('.vt-vid', c));
  const prev = $('.vt-prev', car), next = $('.vt-next', car);
  /* the strip opens on the card the markup flags is-active, else its second */
  const flagged = originals.findIndex(c => c.classList.contains('is-active'));
  let active = N + (flagged >= 0 ? flagged : Math.min(1, N - 1));
  let inView = false;
  let userPaused = false;
  let mutedAll = true;   /* silent until the visitor touches the rail */
  /* Reduced motion or Save-Data: the rail holds its posters — no autoplay, no buffers —
     until the visitor taps, steps or swipes; from then on it runs as it always has,
     under the same sound rules. */
  const checkRailMotionOK = () => !RM && !checkSaveData();
  let userStarted = false;
  let parkedAt = 0;   /* the active clip's playhead while the rail is out of view */

  const centre = () => {
    const base = cards[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(track).gap) || 22;
    const x = viewport.clientWidth / 2 - (active * (base + gap) + base / 2);
    track.style.transform = `translateX(${x}px)`;
    cards.forEach((c, i) => c.classList.toggle('is-active', i === active));
  };

  const playActive = () => {
    vids.forEach((v, i) => { if (v && i !== active) v.pause(); });
    const v = vids[active];
    if (v && inView && !userPaused && (userStarted || checkRailMotionOK())) {
      /* the shared mute is re-asserted at the moment of play — autoplay
         must never arrive with its own sound */
      v.muted = mutedAll;
      v.play().catch(() => {});
    }
  };

  /* If the active index has left the middle copy, jump a whole period back
     into it with the transition off — the strip repeats every N cards, so
     the pixels are identical. Every column's playhead is copied to its
     twin, not just the active one, so the peeking neighbours land on the
     same frame too. Returns the index shift so callers can remap a
     clicked card. */
  const snap = () => {
    if (active >= N && active < 2 * N) return 0;
    const from = active;
    active = ((active % N) + N) % N + N;
    const d = from - active;   /* ±N: toward the copy being left behind */
    for (let i = N; i < 2 * N; i++) {
      const twin = vids[i + d];
      if (vids[i] && twin) { try { vids[i].currentTime = twin.currentTime || 0; } catch (_) {} }
    }
    track.style.transition = 'none';
    centre();
    void track.offsetHeight;
    track.style.transition = '';
    warm();
    playActive();
    return active - from;
  };

  /* Only the active clip holds a media source. Neighbouring and duplicate cards
     keep their authored posters without allocating media pipelines. Previously,
     the active clip and its two neighbours held media, and only the active one
     was intended to buffer. A neighbour keeps its src attached at preload="metadata" and is
     never load()ed: measured on iOS 17, a <video> whose src is set fetches only
     its metadata whatever preload says, while load() — again whatever preload
     says, even "none" — buffers the WHOLE clip (a 56 s / 10.8 MB story clip in
     4 s; one scroll past this rail moved 25 MB). The poster covers a neighbour
     until it becomes active, and playActive()'s play() is what fetches it.
     Every other column is torn down to its poster. Without the teardown a lap
     around the strip left two dozen buffers and decoders alive, and the browser
     starved the next play() for ten seconds or more. Each card remembers its
     clip in data-src while it is cold. */
  const frameCovers = new Map(vids.filter(Boolean).map(video => [video, createTestimonialFrameCover(video)]));
  vids.forEach((v) => { if (v && !v.dataset.src) v.dataset.src = v.getAttribute('src') || ''; });
  const wake = (v, playing) => {
    const coldSources = v.querySelectorAll('source[data-src]:not([src])');
    if ((!v.getAttribute('src') && v.dataset.src) || coldSources.length) {
      frameCovers.get(v).arm();
      if (v.dataset.src) v.setAttribute('src', v.dataset.src);
      coldSources.forEach(source => source.setAttribute('src', source.dataset.src));
      v.load();
    }
    v.preload = playing ? 'auto' : 'metadata';
  };
  const sleep = (v) => {
    if (!v.getAttribute('src') && !v.querySelector('source[src]')) return;
    frameCovers.get(v).reset();
    v.pause();
    v.removeAttribute('src');
    v.querySelectorAll('source[src]').forEach(source => source.removeAttribute('src'));
    v.preload = 'none';
    try { v.load(); } catch (_) {}     /* Release inactive video buffers. */
  };
  const warm = () => {
    const hold = !userStarted && !checkRailMotionOK();   /* posters until asked */
    vids.forEach((v, i) => {
      if (!v) return;
      if (inView && !hold && i === active) wake(v, true); else sleep(v);
    });
  };

  /* A glide may land on an edge copy and STAY there, playing — teleporting
     mid-dwell swapped a playing clone for a cold twin, and the seek-and-
     load stall read as a stutter right at the loop seam. The fold home is
     the snap() at the start of the next step (every advance path opens
     with one) or an off-screen moment, where identical pixels make it
     invisible. */
  const setActive = (i) => {
    active = Math.max(0, Math.min(cards.length - 1, i));
    parkedAt = 0;   /* a parked playhead belongs to the card that was playing */
    userPaused = false;
    centre();
    warm();
    try { vids[active].currentTime = 0; } catch (_) {}
    playActive();
  };

  const applyMute = () => {
    vids.forEach(v => { if (v) v.muted = mutedAll; });
    cards.forEach(c => {
      const btn = $('.vt-mute', c);
      if (!btn) return;
      const u = $('use', btn);
      if (u) u.setAttribute('href', mutedAll ? '#ic-mute' : '#ic-volume');
      btn.setAttribute('aria-label', mutedAll ? 'Unmute' : 'Mute');
    });
  };

  /* The first touch of the rail brings the sound up — autoplay must never
     arrive with its own sound, but a visitor who steps, taps or swipes has
     asked to hear it — unless they have muted, which holds for the visit. */
  const wakeSound = () => {
    userStarted = true;   /* every user path passes through here */
    const want = railSoundChoice === null ? false : railSoundChoice;
    if (mutedAll !== want) { mutedAll = want; applyMute(); }
  };

  /* TikTok-style: sound on -> restart the clip so you hear it from the top */
  const soundOn = () => {
    mutedAll = false;
    applyMute();
    userPaused = false;
    /* a held rail (reduced motion / Save-Data) kept its clips asleep; the
       first ask for sound is also the ask to load them */
    if (!vids[active]?.getAttribute('src')) warm();
    try { vids[active].currentTime = 0; } catch (_) {}
    playActive();
  };

  prev && prev.addEventListener('click', () => { wakeSound(); snap(); setActive(active - 1); });
  next && next.addEventListener('click', () => { wakeSound(); snap(); setActive(active + 1); });

  /* swipe steps the strip — arrows are a stretch on a phone */
  let tX = null, tY = null, swiped = false;
  viewport.addEventListener('touchstart', (e) => {
    tX = e.touches[0].clientX; tY = e.touches[0].clientY;
  }, { passive: true });
  viewport.addEventListener('touchend', (e) => {
    if (tX == null) return;
    const dx = e.changedTouches[0].clientX - tX;
    const dy = e.changedTouches[0].clientY - tY;
    tX = tY = null;
    if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    swiped = true;
    wakeSound();
    snap();
    setActive(active + (dx < 0 ? 1 : -1));
  }, { passive: true });

  cards.forEach((c, i) => {
    let scrubbing = false;
    c.addEventListener('click', (e) => {
      if (swiped) { swiped = false; return; }   /* the tail of a swipe, not a tap */
      if (e.target.closest('.vt-mute') || e.target.closest('.vt-progress')) return;
      const wasSilent = mutedAll;
      const wasHeld = !userStarted && !checkRailMotionOK();
      wakeSound();
      const t = i + snap();
      if (t !== active) { setActive(t); return; }
      if (!vids[active]) return;   /* a card with no clip has nothing to toggle */
      if (wasSilent && !mutedAll) { soundOn(); return; }   /* the tap brought the sound up: hear it from the top, never a pause */
      if (wasHeld) { warm(); playActive(); return; }   /* the poster was waiting for this tap */
      userPaused = !userPaused;
      if (userPaused) vids[active].pause(); else vids[active].play().catch(() => {});
    });
    const mute = $('.vt-mute', c);
    mute && mute.addEventListener('click', (e) => {
      e.stopPropagation();
      railSoundChoice = !mutedAll;   /* their call, kept for the visit */
      const wasHeld = !userStarted && !checkRailMotionOK();
      userStarted = true;
      if (mutedAll) soundOn();
      else { mutedAll = true; applyMute(); }
      if (wasHeld) { warm(); playActive(); }   /* the poster was waiting for this press */
    });
    const prog = $('.vt-progress', c);
    const seek = (e) => {
      const r = prog.getBoundingClientRect();
      const f = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      const v = vids[i];
      if (!v || !v.duration) return;
      try { v.currentTime = f * v.duration; } catch (_) {}
      const bar = $('span', prog);
      if (bar) bar.style.width = (f * 100) + '%';
    };
    prog && prog.addEventListener('pointerdown', (e) => {
      if (i !== active) return;
      scrubbing = true;
      try { prog.setPointerCapture(e.pointerId); } catch (_) {}
      seek(e);
    });
    prog && prog.addEventListener('pointermove', (e) => { if (scrubbing) seek(e); });
    prog && prog.addEventListener('pointerup', () => { scrubbing = false; });
    prog && prog.addEventListener('pointercancel', () => { scrubbing = false; });
    vids[i]?.addEventListener('timeupdate', () => {
      if (i !== active || scrubbing) return;
      const bar = $('.vt-progress span', c);
      if (bar && vids[i].duration) bar.style.width = (vids[i].currentTime / vids[i].duration * 100) + '%';
    });
    vids[i]?.addEventListener('ended', () => {
      if (i === active && inView && !userPaused && !scrubbing) { snap(); setActive(active + 1); }
    });
    /* a clip that ships a VTT captions into the glass pill — native cue
       chrome never matches the page. Clones keep their <track> and get
       their own pill here, so a dwell on an edge copy captions itself. */
    /* the voice waveform (.vt-wave): drawn from the clip's own pre-computed
       levels, one base-36 digit per 50 ms, so it moves with the narration
       whether the sound is up or not. Runs a frame loop only while this
       card plays; the class fades the canvas in and out. */
    const wave = $('.vt-wave', c);
    const levels = vids[i] && vids[i].dataset.wave;
    if (wave && levels && !RM) {
      const RATE = 20, BARS = 16;
      const level = (k) => {
        if (k < 0 || k >= levels.length) return 0;
        const ch = levels.charCodeAt(k);
        return (ch >= 97 ? ch - 87 : ch - 48) / 35;
      };
      let raf = 0, col = '#fff';
      const draw = () => {
        const v = vids[i];
        const dpr = Math.min(2, devicePixelRatio || 1);
        const w = wave.clientWidth, h = wave.clientHeight;
        if (!w || !h) { raf = requestAnimationFrame(draw); return; }
        if (wave.width !== Math.round(w * dpr) || wave.height !== Math.round(h * dpr)) {
          wave.width = Math.round(w * dpr); wave.height = Math.round(h * dpr);
        }
        const ctx = wave.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        const t = v.currentTime * RATE;
        const k0 = Math.floor(t), f = t - k0;
        const step = w / (2 * BARS + 1);
        const bw = Math.max(2, step * 0.5);
        ctx.lineCap = 'round'; ctx.lineWidth = bw; ctx.strokeStyle = col;
        for (let b = -BARS; b <= BARS; b++) {
          const a = Math.abs(b), k = k0 - a;
          const lv = level(k) * (1 - f) + level(k + 1) * f;
          const len = Math.max(bw, lv * (h - bw));
          const x = w / 2 + b * step;
          ctx.globalAlpha = 1 - (a / (BARS + 2)) * 0.75;
          ctx.beginPath(); ctx.moveTo(x, h / 2 - len / 2); ctx.lineTo(x, h / 2 + len / 2); ctx.stroke();
        }
        raf = requestAnimationFrame(draw);
      };
      const waveOn = () => { col = getComputedStyle(wave).color; wave.classList.add('on'); cancelAnimationFrame(raf); raf = requestAnimationFrame(draw); };
      const waveOff = () => { wave.classList.remove('on'); cancelAnimationFrame(raf); raf = 0; };
      vids[i].addEventListener('playing', waveOn);
      vids[i].addEventListener('pause', waveOff);
      vids[i].addEventListener('ended', waveOff);
      vids[i].addEventListener('emptied', waveOff);
    }
    const cap = $('.vt-cap', c);
    const tt = vids[i] && vids[i].textTracks && vids[i].textTracks[0];
    if (cap && tt) {
      tt.mode = 'hidden';
      tt.addEventListener('cuechange', () => {
        const cue = tt.activeCues && tt.activeCues[0];
        if (!cue) { cap.classList.remove('show'); return; }
        cap.textContent = cue.text.replace(/\n/g, ' ');
        cap.classList.add('show');
        /* re-trigger the per-line pop even between back-to-back cues */
        cap.classList.remove('pop');
        void cap.offsetWidth;
        cap.classList.add('pop');
      });
    }
  });

  applyMute();

  /* Off screen the rail lets every buffer go, not only the distant ones: three live
     clips per rail, two rails, was thirty-odd seconds of video held for strips nobody
     was looking at. The active card's playhead is kept so the return picks the story
     up where it was rather than restarting the clip. */
  const restoreParked = () => {
    const v = vids[active], t = parkedAt;
    parkedAt = 0;
    if (!v || !t) return;
    const seek = () => { try { v.currentTime = t; } catch (_) {} };
    if (v.readyState >= 1) seek(); else v.addEventListener('loadedmetadata', seek, { once: true });
  };
  new IntersectionObserver((es) => {
    inView = es.some(e => e.isIntersecting);
    if (inView) { warm(); restoreParked(); playActive(); }
    else {
      vids.forEach(v => v?.pause());
      snap();                                    /* park mid-copy while unseen */
      parkedAt = vids[active]?.currentTime || 0;
      vids.forEach(v => { if (v) sleep(v); });   /* and let the buffers go */
    }
  }, { threshold: 0.3 }).observe(car);

  addEventListener('resize', centre);
  centre();
});

/* ---------- lab-report library (modal) -----------------------------
   The lab-verified claims behind this page, in an accessible dialog —
   deliberately high level per legal: no laboratory names, no per-study
   figures, no methods, no documents. The one number that may appear is
   the summary claim (kills 99.9% of germs); everything else is a named
   germ or the two-shade whitening promise. Wired to the lab-cert
   button, each pathogen in the S18 carousel, and the whitening results
   link. Germ order is mandated: common cold first, influenza second,
   coronavirus third. */
(() => {
  const GROUPS = [
    {
      title: 'Kills 99.9% of:',
      note: 'Kills 99.9% of germs on the brush head — verified by independent laboratories.',
      items: [
        { slug: 'rhinovirus',  name: 'Rhinovirus',               sub: 'The common cold — spreads by touch' },
        { slug: 'h1n1',        name: 'Influenza',                sub: 'The flu virus — spreads on contact' },
        { slug: 'coronavirus', name: 'Human coronavirus',        sub: 'Airborne — lingers on surfaces' },
        { slug: 'ecoli',       name: 'E. coli',                  sci: true, sub: 'Found in fecal matter' },
        { slug: 'salmonella',  name: 'Salmonella',               sci: true, sub: 'Causes food poisoning' },
        { slug: 'saureus',     name: 'Staphylococcus aureus',    sci: true, sub: 'Staph — causes skin infections' },
        { slug: 'spyogenes',   name: 'Streptococcus pyogenes',   sci: true, sub: 'Causes strep throat' },
        { slug: 'spneumoniae', name: 'Streptococcus pneumoniae', sci: true, sub: 'Linked to pneumonia & ear infections' },
        { slug: 'smutans',     name: 'Streptococcus mutans',     sci: true, sub: 'The main driver of tooth decay' },
        { slug: 'ehirae',      name: 'Enterococcus hirae',       sci: true, sub: 'A tough bacteria — hard to kill' },
        { slug: 'candida',     name: 'Candida albicans',         sci: true, sub: 'Leads to oral thrush & bad breath' },
        { slug: 'herpes',      name: 'Human herpesvirus 1',      sub: 'Causes cold sores' },
      ],
    },
    {
      title: 'Whitening',
      note: 'Brush normally with the included Whitening head.',
      items: [
        { slug: 'whitening', name: 'Visibly whiter teeth', sub: '2 shades in 2 weeks' },
      ],
    },
  ];

  /* carousel <h3> label → report slug */
  const CARD_MAP = {
    'human coronavirus': 'coronavirus',
    'e. coli': 'ecoli',
    'influenza': 'h1n1',
    'salmonella': 'salmonella',
    'streptococcus': 'spyogenes',
    'candida': 'candida',
    'rhinovirus': 'rhinovirus',
  };

  const bySlug = {};
  GROUPS.forEach(g => g.items.forEach(it => { bySlug[it.slug] = it; }));

  /* The original report-card anatomy, kept: name on the left with a plain
     line about what the germ is and does where the strain and lab used to
     sit, and the right-hand column — divider and all — holding a check
     where the percentage once was. */
  const row = (it) => {
    const org = it.sci ? `<em>${it.name}</em>` : it.name;
    const sub = it.sub ? `<small>${it.sub}</small>` : '';
    return `<li class="lr-row" id="lr-row-${it.slug}">
      <span class="lr-org"><span class="lr-name">${org}</span>${sub}</span>
      <span class="lr-chip"><svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 1.7l2.1 1.5 2.5-.6 1.2 2.3 2.5.6v2.6l1.8 1.9-1.2 2.3 1.2 2.3-1.8 1.9v2.6l-2.5.6-1.2 2.3-2.5-.6-2.1 1.5-2.1-1.5-2.5.6-1.2-2.3-2.5-.6v-2.6l-1.8-1.9 1.2-2.3-1.2-2.3 1.8-1.9V5.5l2.5-.6 1.2-2.3 2.5.6L12 1.7z"/><path d="M7.6 12.4l3 3 5.8-6.2" fill="none" stroke="var(--accent-ink, #fff)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg><small>Lab verified</small></span>
    </li>`;
  };

  const groupHtml = (g) => `<section class="lr-group">
      <div class="lr-group-head"><h3>${g.title}</h3><p>${g.note}</p></div>
      <ul class="lr-list">${g.items.map(row).join('')}</ul>
    </section>`;

  const modal = document.createElement('div');
  modal.className = 'lr-modal';
  modal.id = 'lr-modal';
  modal.hidden = true;
  modal.innerHTML = `
    <div class="lr-backdrop" data-lr-close></div>
    <div class="lr-panel" role="dialog" aria-modal="true" aria-labelledby="lr-title" tabindex="-1">
      <header class="lr-head">
        <div>
          <p class="lr-kicker"><svg viewBox="0 0 24 24" aria-hidden="true"><use href="#ic-shield"/></svg>Lab verified</p>
          <h2 id="lr-title">Independent Lab Results</h2>
          <p class="lr-intro">The TAO&nbsp;Clean UV-C base and brush are tested by independent laboratories.</p>
        </div>
        <button class="lr-close" type="button" data-lr-close aria-label="Close lab reports">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
      </header>
      <div class="lr-body">${GROUPS.map(groupHtml).join('')}</div>
      <p class="lr-foot">Results reflect independent testing of the TAO&nbsp;Clean UV-C base and brush heads.</p>
    </div>`;
  document.body.appendChild(modal);

  const panel = $('.lr-panel', modal);
  let lastFocus = null;

  const focusables = () => $$('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
    .filter(el => el.offsetParent !== null);

  const open = (slug) => {
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('lr-open');
    void panel.offsetWidth;                 // flush styles so the panel transitions in from hidden
    modal.classList.add('is-open');         // not gated on rAF: reveals must land even if frames stall
    const target = slug && $(`#lr-row-${slug}`, modal);
    if (target) {
      target.classList.add('is-cited');
      target.scrollIntoView({ block: 'center', behavior: RM ? 'auto' : 'smooth' });
      setTimeout(() => target.classList.remove('is-cited'), 2400);
      panel.focus({ preventScroll: true });
    } else {
      $('.lr-body', modal).scrollTop = 0;   // always reopen at the top
      panel.focus({ preventScroll: true });
    }
  };

  const close = () => {
    modal.classList.remove('is-open');
    document.body.classList.remove('lr-open');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      modal.hidden = true;
      modal.removeEventListener('transitionend', finish);
    };
    if (RM) finish();
    else { modal.addEventListener('transitionend', finish); setTimeout(finish, 420); }
    lastFocus?.focus?.({ preventScroll: true });
  };

  modal.addEventListener('click', (e) => { if (e.target.closest('[data-lr-close]')) close(); });
  addEventListener('keydown', (e) => {
    if (modal.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    const f = focusables();
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* triggers: any [data-lab-open] element opens the library (optional
     data-report="<slug>" cites a specific study) */
  $$('[data-lab-open]').forEach(btn => {
    btn.addEventListener('click', (e) => { e.preventDefault(); open(btn.dataset.report || ''); });
  });

  /* S18 carousel: turn each mapped pathogen card into a report trigger */
  $$('.lab-carousel .lp-item').forEach(item => {
    const label = $('h3', item)?.textContent.trim().toLowerCase();
    const slug = label && CARD_MAP[label];
    if (!slug || !bySlug[slug]) return;
    item.classList.add('lp-hasreport');
    const status = $('.lp-status', item);
    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'lp-report';
    cta.setAttribute('aria-label', `View the lab report for ${$('h3', item).textContent.trim()}`);
    cta.innerHTML = `View lab report<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    cta.addEventListener('click', () => open(slug));
    (status?.parentNode || item).insertBefore(cta, status ? status.nextSibling : null);
  });
})();

/* ── fit build: pdp-science (script.js) ── */
/* ---------- science reasons rail (S7, phones) ----------------------
   The four reasons ride a scroll-snap rail under the UV footage (pdp.css,
   ≤ 920px). Swiping is native; this owns only the dots and a gentle
   auto-advance: one card every 4 s while the rail is at least half on
   screen, off while the section carries .is-far, under reduced motion,
   with the tab hidden, or on desktop widths. A finger on the rail stops
   the clock and a tap on a dot or a swipe pushes the next advance out to
   7 s. The active card is read through an IntersectionObserver rooted in
   the rail, so nothing measures layout per frame; the one layout read
   (offsetLeft) happens per advance. */
(() => {
  const section = document.getElementById('science');
  const rail = section && section.querySelector('.sci-rail');
  if (!rail) return;
  const slides = [...rail.children].filter((el) => el.tagName === 'LI');
  const dots = [...section.querySelectorAll('.sci-dot')];
  if (slides.length < 2) return;

  const PHONE = matchMedia('(max-width: 920px)');
  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)');
  const DWELL = 4000;   /* between auto-advances */
  const HOLD = 7000;    /* after the reader touched, swiped or tapped a dot */
  let cur = 0;
  let timer = 0;
  let inView = false;
  let held = false;
  let ownScrollAt = -Infinity;

  const paint = (i) => {
    cur = i;
    dots.forEach((d, k) => {
      d.classList.toggle('is-on', k === i);
      if (k === i) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  };

  const go = (i, smooth) => {
    const n = slides.length;
    const k = ((i % n) + n) % n;
    const left = slides[k].offsetLeft;   /* the rail is position:relative, so this is the snap point */
    ownScrollAt = performance.now();
    try {
      rail.scrollTo({ left, behavior: smooth && !REDUCE.matches ? 'smooth' : 'auto' });
    } catch (e) {
      rail.scrollLeft = left;
    }
    paint(k);
  };

  const isFar = () => section.classList.contains('is-far') || Boolean(section.closest('.is-far'));
  const allowed = () => PHONE.matches && !REDUCE.matches && inView && !held && !document.hidden && !isFar();

  const stop = () => { clearTimeout(timer); timer = 0; };
  const tick = () => {
    timer = 0;
    if (!allowed()) return;
    go(cur + 1, true);
    arm(DWELL);
  };
  const arm = (delay) => {
    stop();
    if (!allowed()) return;
    timer = setTimeout(tick, delay);
  };

  /* the dots follow whichever card the snap settled on — swipe or ours */
  if ('IntersectionObserver' in window) {
    const activeIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) paint(slides.indexOf(en.target)); });
    }, { root: rail, threshold: 0.6 });
    slides.forEach((s) => activeIO.observe(s));

    /* the clock only runs while the rail itself is on screen; the section's
       .is-far (pdp.js, 50% margin) is checked again at every tick */
    const viewIO = new IntersectionObserver((entries) => {
      inView = entries.some((e) => e.isIntersecting);
      if (inView) arm(DWELL); else stop();
    }, { threshold: 0.5 });
    viewIO.observe(rail);
  }

  /* a finger on the rail pauses it; the clock comes back a while after it lifts */
  const hold = () => { held = true; stop(); };
  const release = () => { held = false; arm(HOLD); };
  ['pointerdown', 'touchstart'].forEach((t) => rail.addEventListener(t, hold, { passive: true }));
  ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach((t) => rail.addEventListener(t, release, { passive: true }));
  /* momentum keeps the rail scrolling after the finger lifts: any scroll that
     is not our own smooth scroll pushes the next advance out */
  rail.addEventListener('scroll', () => {
    if (performance.now() - ownScrollAt < 900) return;
    if (!held) arm(HOLD);
  }, { passive: true });

  dots.forEach((d, k) => d.addEventListener('click', () => { go(k, true); arm(HOLD); }));

  document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); else arm(DWELL); });
  const onMedia = () => { if (allowed()) arm(DWELL); else stop(); };
  if (typeof REDUCE.addEventListener === 'function') {
    REDUCE.addEventListener('change', onMedia);
    PHONE.addEventListener('change', onMedia);
  }

  paint(0);
})();

/* ── fit build: pdp-compare (script.js) ── */
/* ---------- comparison spec sheet: rows scroll inside the card ----------
   Phones (pdp.css ≤560) size the "See the difference" card to the screen and let
   its eight rows scroll under a pinned column header. This marks the shell
   .cmpf-scrolls when the body actually overflows (once at load, once per resize,
   one layout read each, rAF-coalesced), .cmpf-at-end when the last row is fully in
   view (IntersectionObserver rooted on the scroller — no scroll listener), and
   wires the floating "more" button to page the scroller. Safe when the section
   is absent or the CSS is not applied: nothing is shown and nothing throws. */
(function () {
  var shell = document.querySelector('.compare [data-cmpf-shell]');
  if (!shell || !('IntersectionObserver' in window)) return;
  var body = shell.querySelector('[data-cmpf-body]');
  var more = shell.querySelector('[data-cmpf-more]');
  if (!body || !more) return;
  var rows = body.querySelectorAll('.cmp-row');
  var last = rows[rows.length - 1];
  if (!last) return;

  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var scrolls = false;
  var atEnd = false;
  var raf = 0;

  var paint = function () {
    shell.classList.toggle('cmpf-scrolls', scrolls);
    shell.classList.toggle('cmpf-at-end', atEnd);
    more.hidden = !(scrolls && !atEnd);
    if (scrolls) body.setAttribute('tabindex', '0');
    else body.removeAttribute('tabindex');
  };

  /* one layout read: does the body overflow at this viewport? */
  var measure = function () {
    raf = 0;
    scrolls = body.scrollHeight - body.clientHeight > 4;
    paint();
  };
  var queue = function () {
    if (!raf) raf = requestAnimationFrame(measure);
  };

  /* the last row fully inside the scroller = the end is reached */
  var io = new IntersectionObserver(function (entries) {
    var en = entries[entries.length - 1];
    if (!en) return;
    atEnd = en.isIntersecting && en.intersectionRatio >= 0.96;
    paint();
  }, { root: body, threshold: [0, 0.96, 1] });
  io.observe(last);

  more.addEventListener('click', function () {
    var view = body.clientHeight;
    var max = body.scrollHeight - view;
    var top = Math.min(body.scrollTop + Math.max(80, view - 40), max);
    if (typeof body.scrollTo === 'function') {
      body.scrollTo({ top: top, behavior: (reduce && reduce.matches) ? 'auto' : 'smooth' });
    } else {
      body.scrollTop = top;
    }
  });

  window.addEventListener('resize', queue, { passive: true });
  window.addEventListener('orientationchange', queue, { passive: true });
  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(queue, function () {});
  }
  queue();
})();

/* ── fit build: pdp-colors (script.js) ── */
/* ── fit build: pdp-colors (script.js) ─────────────────────────────────────────────
   Phone colour grid: the .cw-card buttons show only their swatch, so the chosen
   colorway's name, badge and tier are mirrored into #cs-pick. Reads the pressed card
   (pdp.js's syncTheme sets aria-pressed on every card at each commit) — no knowledge
   of THEMES, no layout reads; 16 attribute mutations per switch coalesce into one
   rAF. Harmless on desktop, where CSS hides #cs-pick. */
(() => {
  const strip = document.getElementById('tier-groups');
  const pick = document.getElementById('cs-pick');
  if (!strip || !pick || typeof MutationObserver !== 'function') return;
  const nameEl = pick.querySelector('.cs-pick-name');
  const badgeEl = pick.querySelector('.cs-pick-badge');
  const tierEl = pick.querySelector('.cs-pick-tier');
  const text = (el) => (el && el.textContent ? el.textContent.trim() : '');
  let raf = 0;
  const update = () => {
    raf = 0;
    const sel = strip.querySelector('.cw-card[aria-pressed="true"]');
    if (!sel) return;
    const name = text(sel.querySelector('.cw-name')) || sel.dataset.id || '';
    const badge = text(sel.querySelector('.cw-badge'));
    let head = sel.previousElementSibling;
    while (head && !head.classList.contains('cstrip-head')) head = head.previousElementSibling;
    const tier = text(head && head.querySelector('h3'));
    if (nameEl && nameEl.textContent !== name) nameEl.textContent = name;
    if (badgeEl) {
      if (badgeEl.textContent !== badge) badgeEl.textContent = badge;
      badgeEl.hidden = !badge;
    }
    if (tierEl) {
      if (tierEl.textContent !== tier) tierEl.textContent = tier;
      tierEl.hidden = !tier;
    }
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
  try {
    new MutationObserver(schedule).observe(strip, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['aria-pressed'],
    });
  } catch (_) { /* no observer: the boot fill below still runs */ }
  schedule();
})();

/* ── fit build: pdp-reviews (script.js) ── */
/* ── fit build: pdp-reviews (script.js) ──────────────────────────────────────
   Appended to assets/tao-mockup-pdp.js, so it runs AFTER the section's own
   filter/expander block. Two guards around the phone review rail; the swipe
   itself is left entirely to the browser — nothing here reads or writes
   .rev-grid.scrollLeft while a gesture is in flight, and nothing listens to
   scroll.

   1. A filter chip swaps the contents of the rail. The browser keeps the old
      offset, so picking "Shipping" from three cards in leaves you looking at
      the middle of a two-card list (or, once the offset is clamped, at a
      half-snapped position). Sending the rail back to the first card is the
      only sane resting place for a new list.

   2. "See all reviews" ends with pdp.js handing focus to the first newly
      revealed card: `first.focus({ preventScroll: false })`. On the desktop
      grid that nudges the page down to the new rows. On the phone rail that
      same call flings the rail six cards along AND scrolls the page, because
      the card it focuses is the seventh one, off to the right. The reader is
      put back where they were; the focus (and its announcement) is left alone.

   Both are phone-only, both are no-ops if the section is absent, and neither
   throws if the elements are missing. */
(function () {
  var rail = document.getElementById('rev-grid');
  if (!rail) return;

  var section = rail.closest ? rail.closest('.reviews') : null;
  var phone = function () {
    return !window.matchMedia || window.matchMedia('(max-width: 920px)').matches;
  };
  var toStart = function () {
    if (rail.scrollLeft !== 0) rail.scrollLeft = 0;
  };

  /* ---- 1 · a new topic starts at the first review ---- */
  var chips = section ? section.querySelectorAll('.rev-filters .chip') : [];
  Array.prototype.forEach.call(chips, function (chip) {
    chip.addEventListener('click', function () {
      if (!phone()) return;
      /* after pdp.js has re-hidden/shown the cards and the rail has been laid
         out again — one frame, no forced reflow of our own */
      requestAnimationFrame(toStart);
    });
  });

  /* ---- 2 · "See all reviews" does not teleport the rail or the page ----
     Listened for on the document, not on the button: at AT_TARGET both capture
     and bubble listeners run in registration order, so a capture listener on
     the button itself would still fire after pdp.js's. On an ancestor it is a
     real capture phase and runs first. */
  var more = document.getElementById('rev-more');
  if (more) {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest || !t.closest('#rev-more')) return;
      if (!phone()) return;

      var x = rail.scrollLeft;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var root = document.documentElement;
      var prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';   /* the theme sets html{scroll-behavior:smooth} */

      var restore = function () {
        if (rail.scrollLeft !== x) rail.scrollLeft = x;
        var now = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (Math.abs(now - y) > 1) window.scrollTo(0, y);
      };

      /* focusin fires inside focus(), so in practice the page never paints the
         displaced frame; the rAF is the belt to that pair of braces */
      document.addEventListener('focusin', restore, true);
      requestAnimationFrame(function () {
        document.removeEventListener('focusin', restore, true);
        restore();
        root.style.scrollBehavior = prevBehavior;
      });
    }, true);   /* capture: read the offsets before pdp.js moves focus */
  }
})();

/* ── fit build: round 2 — features rail (script.js) ── */
/* Phones (≤560): the four feature cards ride a scroll-snap rail, one per screen, with
   dots and a gentle auto-advance (4.2 s while the strip is in view; a touch, a swipe or
   a dot pushes the next advance out to 8 s). The alpha badge clips stay <video>s here
   (pdp.js no longer swaps them for posters at this width) and only the card in view
   plays: its <source media> gates come off on first show, so nothing loads before it
   is looked at — ~0.7 MB per clip, one at a time. Desktop keeps its own player. */
(() => {
  const PHONE = matchMedia('(max-width: 560px)');
  if (!PHONE.matches) return;
  const card = document.querySelector('.tao-product-new .feature-card');
  if (!card) return;
  const feats = [...card.querySelectorAll('.feat')];
  if (feats.length < 2) return;
  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)');
  const vids = feats.map((f) => f.querySelector('video.feat-vid'));
  const DWELL = 4200, HOLD = 8000;
  let cur = 0, timer = 0, inView = false, held = false, ownScrollAt = -Infinity;

  const dots = document.createElement('div');
  dots.className = 'feat-dots';
  dots.setAttribute('role', 'group');
  dots.setAttribute('aria-label', 'Choose a feature');
  feats.forEach((f, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'feat-dot' + (i ? '' : ' is-on');
    const h = f.querySelector('h3');
    b.setAttribute('aria-label', `${h ? h.textContent.trim() : 'Feature'}, ${i + 1} of ${feats.length}`);
    if (!i) b.setAttribute('aria-current', 'true');
    b.addEventListener('click', () => { go(i, true); held = true; arm(HOLD, true); });
    dots.appendChild(b);
  });
  card.after(dots);

  const arm = (delay, release) => {
    clearTimeout(timer); timer = 0;
    if (release) held = false;
    if (!allowed()) return;
    timer = setTimeout(tick, delay);
  };
  const stop = () => { clearTimeout(timer); timer = 0; };
  const allowed = () => PHONE.matches && !REDUCE.matches && inView && !held && !document.hidden;
  const tick = () => { timer = 0; if (!allowed()) return; go(cur + 1, true); arm(DWELL); };

  const armClip = (v) => {
    if (!v || v.dataset.armed) return;
    v.dataset.armed = '1';
    v.querySelectorAll('source[media]').forEach((s) => s.removeAttribute('media'));
    v.preload = 'auto';
    try { v.load(); } catch (e) {}
  };
  const syncClips = () => {
    vids.forEach((v, i) => {
      if (!v) return;
      if (i === cur && inView && !document.hidden && !REDUCE.matches) { armClip(v); v.play().catch(() => {}); }
      else v.pause();
    });
  };
  const paint = (i) => {
    cur = i;
    [...dots.children].forEach((d, k) => {
      d.classList.toggle('is-on', k === i);
      if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
    });
    syncClips();
  };
  const go = (i, smooth) => {
    const n = feats.length, k = ((i % n) + n) % n;
    const left = feats[k].offsetLeft - feats[0].offsetLeft;
    ownScrollAt = performance.now();
    try { card.scrollTo({ left, behavior: smooth && !REDUCE.matches ? 'smooth' : 'auto' }); }
    catch (e) { card.scrollLeft = left; }
    paint(k);
  };

  if ('IntersectionObserver' in window) {
    const activeIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) paint(feats.indexOf(en.target)); });
    }, { root: card, threshold: 0.6 });
    feats.forEach((f) => activeIO.observe(f));
    const viewIO = new IntersectionObserver((entries) => {
      inView = entries.some((e) => e.isIntersecting);
      if (inView) arm(DWELL); else stop();
      syncClips();
    }, { threshold: 0.5 });
    viewIO.observe(card);
  } else {
    inView = true; syncClips();
  }
  ['pointerdown', 'touchstart'].forEach((t) => card.addEventListener(t, () => { held = true; stop(); }, { passive: true }));
  ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach((t) => card.addEventListener(t, () => arm(HOLD, true), { passive: true }));
  card.addEventListener('scroll', () => {
    if (performance.now() - ownScrollAt < 900) return;
    if (!held) arm(HOLD, true);
  }, { passive: true });
  document.addEventListener('visibilitychange', () => { syncClips(); if (!document.hidden) arm(DWELL); else stop(); });
})();

/* ── fit build: round 2 — colour shots rail (script.js) ── */
/* Phones (≤560): the five colour photographs are pages on a scroll-snap rail (pdp.css
   lays .cshow out as a flex row there), so the FLIP swap — a tap on a flanking shot
   trades it with the centre, re-fetching the full-size picture — never runs: that
   flight, and the 1600-wide decodes it asked for, is the likeliest cause of the
   reloads Ryan saw on his iPhone. Clicks and key presses on the tiles are stopped on
   the document in the capture phase, before .cshow's own listeners see them; the
   tiles drop their button semantics; dots track the shot in view. */
(() => {
  const RAIL = matchMedia('(max-width: 560px)');
  if (!RAIL.matches) return;
  const show = document.querySelector('.tao-product-new .colorshop .cshow');
  if (!show) return;
  const tiles = [...show.querySelectorAll('.cs-g')];
  if (tiles.length < 2) return;
  const block = (e) => {
    if (!RAIL.matches || !e.target || !e.target.closest) return;
    if (!e.target.closest('.colorshop .cshow .cs-g')) return;
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    e.stopImmediatePropagation();
  };
  document.addEventListener('click', block, true);
  document.addEventListener('keydown', block, true);
  tiles.forEach((t) => { t.removeAttribute('role'); t.removeAttribute('tabindex'); t.removeAttribute('aria-label'); });

  /* light arrows over the shots, like the hero's: a page per tap */
  const wrap = document.createElement('div');
  wrap.className = 'cs-railwrap';
  show.parentNode.insertBefore(wrap, show);
  wrap.appendChild(show);
  [['prev', 'Previous photo', -1], ['next', 'Next photo', 1]].forEach(([dir, label, sign]) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `cs-nav cs-nav-${dir}`;
    b.setAttribute('aria-label', label);
    b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><use href="#ic-chevron"/></svg>';
    b.addEventListener('click', () => {
      const w = show.clientWidth;
      const max = show.scrollWidth - w;
      let left = Math.round(show.scrollLeft / w) * w + sign * w;
      if (left < -1) left = max; else if (left > max + 1) left = 0;    /* wrap at the ends */
      try { show.scrollTo({ left, behavior: 'smooth' }); } catch (e) { show.scrollLeft = left; }
    });
    wrap.appendChild(b);
  });

  /* visual order: the pack shot first (pdp.css gives [data-slot="big"] order:-1) */
  const visual = tiles.slice().sort((a, b) => (a.dataset.slot === 'big' ? -1 : 0) - (b.dataset.slot === 'big' ? -1 : 0));
  const names = { big: 'Pack shot', l1: 'Brush head detail', l2: 'In the bathroom', r1: 'The base', r2: 'In hand' };
  const dots = document.createElement('div');
  dots.className = 'cs-dots';
  dots.setAttribute('role', 'group');
  dots.setAttribute('aria-label', 'Choose a photo');
  visual.forEach((t, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cs-dot' + (i ? '' : ' is-on');
    b.setAttribute('aria-label', `${names[t.dataset.slot] || 'Photo'}, ${i + 1} of ${visual.length}`);
    if (!i) b.setAttribute('aria-current', 'true');
    b.addEventListener('click', () => {
      const left = t.offsetLeft - visual[0].offsetLeft;
      try { show.scrollTo({ left, behavior: 'smooth' }); } catch (e) { show.scrollLeft = left; }
    });
    dots.appendChild(b);
  });
  show.after(dots);
  const paint = (i) => {
    [...dots.children].forEach((d, k) => {
      d.classList.toggle('is-on', k === i);
      if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
    });
  };
    let galleryThemeId = currentId;
    function handleGalleryTheme(event) {
        const nextId = event.detail?.id;
        if (!nextId || nextId === galleryThemeId) return;
        galleryThemeId = nextId;
        // Cancel an in-flight swipe without scrolling through the intervening photos.
        show.scrollTo({ left: 0, behavior: 'instant' });
        paint(0);
    }
    addEventListener('tao:theme', handleGalleryTheme);

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        // Read the current position: queued entries can predate a color-change reset.
        const firstLeft = visual[0].offsetLeft;
        let nearestIndex = 0;
        let nearestDistance = Infinity;
        visual.forEach(function findVisiblePhoto(tile, index) {
            const distance = Math.abs(tile.offsetLeft - firstLeft - show.scrollLeft);
            if (distance < nearestDistance) {
                nearestIndex = index;
                nearestDistance = distance;
            }
        });
        paint(nearestIndex);
    }, { root: show, threshold: 0.6 });
    visual.forEach((t) => io.observe(t));
  }
})();

/* ── fit build: round 2 — "See all reviews" modal (script.js) ── */
/* The button opens the store's Junip review widget in a modal (every width). The
   markup lives in the reviews section so Junip's onsite script finds its mount at
   load; it is moved to <body> here because a fixed overlay inside a section would sit
   in that section's stacking context. The click is caught on the document in the
   capture phase and stopped, so pdp.js's older expand-the-grid handler never runs.
   If the widget has not rendered ~1.2 s after opening (Junip off, blocked, slow), the
   page's own review cards are listed instead. */
(() => {
  const modal = document.getElementById('rev-modal');
  const more = document.getElementById('rev-more');
  if (!modal || !more) return;
  document.body.appendChild(modal);
  const panel = modal.querySelector('.rev-modal-panel');
  const body = modal.querySelector('.rev-modal-body');
  let lastFocus = null, closeTimer = 0;
  const fallback = () => {
    if (body.dataset.fallback) return;
    setTimeout(() => {
      const mount = modal.querySelector('.junip-product-review');
      if (mount && (mount.classList.contains('junip-product-review-rendered') || mount.children.length)) return;
      if (body.dataset.fallback) return;
      body.dataset.fallback = '1';
      const list = document.createElement('div');
      list.className = 'rev-modal-list';
      document.querySelectorAll('#reviews .rev-card').forEach((c) => {
        const cl = c.cloneNode(true);
        cl.hidden = false;
        cl.removeAttribute('data-rv');
        cl.removeAttribute('tabindex');
        cl.classList.remove('rev-flip', 'rv-in', 'rv-noanim');
        cl.style.removeProperty('--fi');
        list.appendChild(cl);
      });
      body.appendChild(list);
    }, 1200);
  };
  const open = () => {
    clearTimeout(closeTimer);
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('rev-open');
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      try { panel.focus({ preventScroll: true }); } catch (e) {}
    });
    fallback();
  };
  const close = () => {
    modal.classList.remove('is-open');
    document.body.classList.remove('rev-open');
    closeTimer = setTimeout(() => { modal.hidden = true; }, 340);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  };
  document.addEventListener('click', (e) => {
    if (!e.target || !e.target.closest || !e.target.closest('#rev-more')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    open();
  }, true);
  modal.addEventListener('click', (e) => { if (e.target.closest('[data-rev-close]')) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); });
})();

/* ── fit build: round 3 — rail posters and the pro crops (script.js) ── */
/* Every clip on the two testimonial rails (customers and dental pros) gets its poster
   drawn as an <img> ON TOP of the <video>. The rail keeps neighbours at metadata only
   and play() is what fetches the first frames, so on iOS the moment a card became
   active it painted black until those frames arrived; the poster now stays until the
   clip has a frame (timeupdate past 0.05 s) and comes back when the rail tears the
   clip down (emptied). Phones also tag the pro cards for their crops: Judi's film
   shows its lower part, the spoken portraits keep their tops. */
(() => {
  const vids = [...document.querySelectorAll('.vt-carousel .vt-media > video.vt-vid')];
  if (!vids.length) return;
  vids.forEach((v) => {
    const media = v.parentElement;
    if (media.closest('.pros')) {
      const label = (v.getAttribute('aria-label') || '').toLowerCase();
      if (label.includes('judi')) media.classList.add('crop-top');
      else if (label.includes('portrait')) media.classList.add('crop-bottom');
    }
    const poster = v.getAttribute('poster');
    if (!poster || media.querySelector('.vt-poster')) return;
    const im = document.createElement('img');
    im.className = 'vt-poster';
    im.src = poster;
    im.alt = '';
    im.decoding = 'async';
    im.setAttribute('aria-hidden', 'true');
    v.after(im);
    const off = () => { if (!v.paused && v.currentTime > 0.05 && v.readyState >= 3) im.classList.add('is-off'); };
    v.addEventListener('timeupdate', off);
    v.addEventListener('playing', () => requestAnimationFrame(off));
    v.addEventListener('emptied', () => im.classList.remove('is-off'));
  });
})();

/* ── fit build: round 4 — pathogen stagger index (script.js) ── */
/* The lab's check-draw, pop and halo animations are offset by --i per card; the markup
   never carried it, so every card ran in lockstep. Numbered here in visual order. */
(() => {
  const items = document.querySelectorAll('.lab-carousel .lp-item');
  items.forEach((it, k) => { if (!it.style.getPropertyValue('--i')) it.style.setProperty('--i', k); });
})();
